import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import MigrationConciergeFloat from '../components/MigrationConciergeFloat';
import MigrationFitWizard from '../components/MigrationFitWizard';
import MigrationCrunchFooter from '../components/MigrationCrunchFooter';
import EmailGateModal from '../components/EmailGateModal';
import ConciergeIntakeModal from '../components/ConciergeIntakeModal';
import MigrationSavingsCalculator from '../components/MigrationSavingsCalculator';
import ExportGuideSection from '../components/ExportGuideSection';
import { MIGRATION_PAIRS, getVocabMap } from '../data/migration_pairs';
import {
  Sparkle, ArrowRight, CheckCircle, WarningCircle, UploadSimple,
  Lightning, Database, Trophy, Clock, CurrencyDollar, FileArrowDown, X,
  Shield, Copy, Play
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api/migrate-pro` });

// Concierge add-on: flat done-for-you fee per tier band (no per-record
// charges). Unlimited AI includes it free. Mirrors CONCIERGE_ADDON on the backend.
const CONCIERGE_ADDONS = { contacts_only: 500, starter_pro: 500, growth_ai: 750, enterprise_ai: 1000 };
const TIER_PRICING = {
  contacts_only: { name: 'Contacts Only', amount: 299 },
  starter_pro: { name: 'Starter Pro', amount: 599 },
  growth_ai: { name: 'Growth AI', amount: 1799 },
  enterprise_ai: { name: 'Enterprise AI', amount: 4499 },
};

// "Add Concierge" checkbox rendered inside each paid tier card.
function ConciergeAddonCheckbox({ tier, price, checked, onChange }) {
  return (
    <label className="flex items-start gap-2 mb-3 cursor-pointer group" data-testid={`mc-addon-${tier}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-[#F39C12] cursor-pointer"
        data-testid={`mc-addon-${tier}-checkbox`}
      />
      <span className="text-[11px] leading-snug text-white/70 group-hover:text-white/90 transition-colors">
        <strong className="text-[#F39C12]">Add Concierge +${price.toLocaleString()}</strong> — a specialist
        runs the whole migration with you on a scheduled call.{' '}
        <a
          href="/migrate/concierge"
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="underline text-white/50 hover:text-amber-300"
          data-testid={`mc-addon-${tier}-how`}
        >
          How it works
        </a>
      </span>
    </label>
  );
}

// Demo video styled like the SalesCrunch tutorial cards: big play button
// overlay + duration badge so it reads unmistakably as a video on the dark page.
function McDemoVideo() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden bg-white shadow-2xl ring-1 ring-white/25" data-testid="mc-demo-video-card">
      <div className="relative bg-slate-900 group">
        <video
          ref={videoRef}
          controls
          preload="metadata"
          poster="/migrationcrunch-demo-poster.jpg"
          className="w-full aspect-video bg-black block"
          data-testid="mc-demo-video"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        >
          <source src="/migrationcrunch-demo.mp4" type="video/mp4" />
        </video>
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="mc-demo-video-overlay">
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative w-20 h-20 rounded-full bg-[#F39C12] shadow-[0_8px_32px_rgba(0,0,0,0.55)] flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play size={34} weight="fill" className="text-slate-900 ml-1" />
            </div>
            <span className="absolute bottom-3 right-3 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded" data-testid="mc-demo-video-duration">1:38</span>
          </div>
        )}
      </div>
      <div className="p-5 flex items-center justify-between gap-4 text-slate-900">
        <div>
          <h3 className="text-base font-bold mb-1">MigrationCrunch demo — 8,500 real contacts</h3>
          <p className="text-sm text-slate-600 leading-relaxed">Upload → AI mapping → duplicate check → clean download. Recorded on the actual product.</p>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-xs font-bold text-slate-500 shrink-0" data-testid="mc-demo-video-time-label">
          <Play size={12} weight="fill" />1:38
        </div>
      </div>
    </div>
  );
}

// Personalized hero copy per source CRM (driven by ?from= URL param + dropdown)
const SOURCE_CRMS = {
  generic: {
    label: 'My CRM (any system)',
    headline: <>Leaving Salesforce or HubSpot? <br /><span className="text-[#F39C12]">Take every note with you.</span></>,
    subhead: <>Export from Salesforce, HubSpot, Pipedrive, or any other CRM as CSV. Our AI maps every field automatically, keeps every note linked to the right contact, and gives you a clean import file ready for <strong className="text-white">SalesCrunchCRM or 11 other destination CRMs</strong> &mdash; Pipedrive, Zoho, Insightly, Copper, Capsule, Less Annoying CRM, Freshsales, Keap, monday.com, or generic CSV. Minutes, not weeks.</>,
  },
  act: {
    label: 'ACT! (3.0, 4.0, 6, 2005-2014)',
    headline: <>Escape ACT! <br /><span className="text-[#F39C12]">with all 25 years of notes intact.</span></>,
    subhead: <>Drop your ACT! <code className="bg-white/10 px-1 rounded text-sm">.dbf</code> files (or PAD/ADF, or a CSV export). Our AI handles the column-name mess automatically, matches your hand-typed history notes to the right contacts, and gives you a clean import file for <strong className="text-white">SalesCrunchCRM, Salesforce, HubSpot, Pipedrive</strong>, or 8 other CRMs. Tested on databases up to 250,000 contacts.</>,
  },
  'act-365': {
    label: 'ACT! 365 / Growth Suite / Premium Cloud',
    headline: <>Bail on ACT! Growth Suite <br /><span className="text-[#F39C12]">before your renewal hits.</span></>,
    subhead: <>Export to CSV from ACT! 365 (or grab the underlying SQL Server backup). Our AI maps every field — including the custom user fields ACT! love-renaming — and gives you a clean import file for <strong className="text-white">SalesCrunchCRM ($30/user/mo)</strong> or 11 other CRMs. Cancel ACT! the same week.</>,
  },
  goldmine: {
    label: 'Goldmine',
    headline: <>Liberate your Goldmine database <br /><span className="text-[#F39C12]">from 1990s database hell.</span></>,
    subhead: <>Export to CSV from Goldmine (Tools → Export Wizard). Our AI handles the GoldMine-specific field naming (Key1-5, MERGECODES, History Type), preserves your User-Defined Fields, and matches activity history to the right contact. Output: a clean CSV ready for <strong className="text-white">SalesCrunchCRM, Salesforce, HubSpot</strong>, or 9 others.</>,
  },
  outlook: {
    label: 'Outlook Contacts / PST',
    headline: <>Get your Outlook contacts <br /><span className="text-[#F39C12]">into a real CRM.</span></>,
    subhead: <>Drop your Outlook <code className="bg-white/10 px-1 rounded text-sm">.pst</code> file in directly &mdash; we parse it natively (no Outlook needed). Or export to CSV from Outlook first. Categories become tags, every note and address survives the trip. Output: a clean import file for <strong className="text-white">SalesCrunchCRM, Salesforce, HubSpot</strong>, or 9 others.</>,
  },
  excel: {
    label: 'Excel / Google Sheets',
    headline: <>Turn your spreadsheet <br /><span className="text-[#F39C12]">into a working CRM.</span></>,
    subhead: <>Upload your <code className="bg-white/10 px-1 rounded text-sm">.xlsx</code> or <code className="bg-white/10 px-1 rounded text-sm">.csv</code> — even if columns are in Spanish, French, or whatever your assistant typed at 3am. Our AI figures out what's a phone, an email, a company, an address. Output: a clean import file for <strong className="text-white">SalesCrunchCRM, Salesforce, HubSpot</strong>, or 9 others.</>,
  },
  salesforce: {
    label: 'Salesforce (too complex)',
    headline: <>Defect from Salesforce <br /><span className="text-[#F39C12]">to a CRM that just works.</span></>,
    subhead: <>Use Salesforce's Data Export, drop the resulting CSV here. Our AI handles Salesforce's verbose field naming and gives you a clean import file for <strong className="text-white">SalesCrunchCRM ($30/user/mo)</strong> or 10 simpler CRMs. The 80% of small businesses paying $150/user/month for features they don't use have a way out now.</>,
  },
  hubspot: {
    label: 'HubSpot (too pricey)',
    headline: <>Leave HubSpot <br /><span className="text-[#F39C12]">before the next price hike.</span></>,
    subhead: <>Export your HubSpot contacts to CSV. Our AI maps every field — including custom HubSpot properties — and gives you a clean import file for <strong className="text-white">SalesCrunchCRM, Pipedrive, Zoho</strong>, or 9 other CRMs. No more $1,200/mo "starter" surprise renewals.</>,
  },
  maximizer: {
    label: 'Maximizer',
    headline: <>Move off Maximizer <br /><span className="text-[#F39C12]">with all your history.</span></>,
    subhead: <>Export to CSV from Maximizer. Our AI handles the unique field structure (UDF prefixes, address blocks), preserves Notes/Calls/Tasks, and matches them to the right contact. Output: clean import file for <strong className="text-white">SalesCrunchCRM, Salesforce, HubSpot</strong>, or 9 others.</>,
  },
  sage: {
    label: 'Sage CRM',
    headline: <>Migrate from Sage CRM <br /><span className="text-[#F39C12]">to a system you actually use.</span></>,
    subhead: <>Sage's CSV export is, kindly, a mess. Our AI sorts it out. Maps Persons + Companies cleanly, dedupes, preserves activity history. Output: a clean import file for <strong className="text-white">SalesCrunchCRM, Salesforce, HubSpot</strong>, or 9 other CRMs.</>,
  },
  zoho: {
    label: 'Zoho CRM',
    headline: <>Switch from Zoho <br /><span className="text-[#F39C12]">in an afternoon.</span></>,
    subhead: <>Use Zoho's bulk export, drop the CSV here. Our AI handles Zoho's "Mailing" address prefixes, custom Lead Source fields, and history. Output: clean CSV for <strong className="text-white">SalesCrunchCRM, Salesforce, HubSpot, Pipedrive</strong>, or 8 others.</>,
  },
  pipedrive: {
    label: 'Pipedrive',
    headline: <>Move off Pipedrive <br /><span className="text-[#F39C12]">without losing your pipeline.</span></>,
    subhead: <>Export Persons + Organizations from Pipedrive (CSV). Our AI splits Pipedrive's combined "Name" field back into First/Last automatically, preserves deals/notes/activities, and gives you a clean file for <strong className="text-white">SalesCrunchCRM, Salesforce, HubSpot</strong>, or 9 others.</>,
  },
};

// Destination dropdown for the generic /migrate hero. pairSeg is the slug
// suffix used to find a dedicated /migrate/{src}-to-{dest} landing page;
// presetId pre-selects the destination in the step-2 wizard.
const DEST_CRMS = [
  { id: '', label: 'Not sure yet' },
  { id: 'salescrunch', label: 'SalesCrunchCRM (free import)', pairSeg: 'act', presetId: 'salescrunch' },
  { id: 'salesforce', label: 'Salesforce', pairSeg: 'salesforce', presetId: 'salesforce' },
  { id: 'hubspot', label: 'HubSpot', pairSeg: 'hubspot', presetId: 'hubspot' },
  { id: 'pipedrive', label: 'Pipedrive', pairSeg: 'pipedrive', presetId: 'pipedrive' },
  { id: 'zoho', label: 'Zoho CRM', pairSeg: 'zoho', presetId: 'zoho' },
  { id: 'insightly', label: 'Insightly', pairSeg: 'insightly', presetId: 'insightly' },
  { id: 'copper', label: 'Copper', pairSeg: 'copper', presetId: 'copper' },
  { id: 'capsule', label: 'Capsule CRM', pairSeg: 'capsule', presetId: 'capsule' },
  { id: 'freshsales', label: 'Freshsales', pairSeg: 'freshsales', presetId: 'freshsales' },
  { id: 'keap', label: 'Keap (Infusionsoft)', pairSeg: 'keap', presetId: 'keap' },
  { id: 'monday', label: 'monday.com', pairSeg: 'monday', presetId: 'monday' },
  { id: 'less-annoying', label: 'Less Annoying CRM', pairSeg: 'less-annoying', presetId: 'generic' },
  { id: 'csv', label: 'Clean CSV (any CRM)', pairSeg: 'generic', presetId: 'generic' },
];

// Maps a SOURCE_CRMS id to the slug prefix used by MIGRATION_PAIRS.
const PAIR_SOURCE_SEG = {
  act: 'act', 'act-365': 'act',
  salesforce: 'salesforce', hubspot: 'hubspot',
  zoho: 'zoho', pipedrive: 'pipedrive',
};

// Sample notes for the "Will my notes survive?" preview — doubles as the
// textarea placeholder and the one-click "Try a sample" fill.
const SAMPLE_PREVIEW_NOTES =
  'Met John Smith at Acme Corp last Tuesday. He referred Sarah Johnson in procurement (sarah.j@acmecorp.com, 555-0123).\n\n' +
  'Quarterly review with Bob Williams of Globex. Wants to loop in Mary Chen next call.\n\n' +
  'Closed TechStart Inc — Jenny at jenny@techstart.io, CFO Mark Davis mentioned.';

/**
 * MigrationCrunch — public standalone product
 * URL: /migrationcrunch (or migrationcrunch.com via DNS)
 * No login. Free up to 50 contacts + full note extraction. $599 flat for unlimited.
 */
export default function MigrationCrunchPage({ pairConfig = null }) {
  // Pair-specific landing pages (e.g. /migrate/act-to-hubspot) pass a
  // pairConfig from MIGRATION_PAIRS. Everything below it adapts: hero
  // copy, locked source/destination, SEO meta tags.
  const [step, setStep] = useState(0); // 0=landing, 1=upload+map, 2=preflight, 3=download
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [licenseKey, setLicenseKey] = useState(localStorage.getItem('mc_license') || '');
  const [licenseInfo, setLicenseInfo] = useState(null);

  const [session, setSession] = useState(null);
  const [mappings, setMappings] = useState({});
  const [expandedSamples, setExpandedSamples] = useState({});  // col → 'loading' | {samples, non_empty_count, total_rows}

  // Fetch distinct values from deeper in the file for one column — powers
  // the "See more values" expander on low-confidence mapping rows.
  const loadColumnSamples = async (col) => {
    setExpandedSamples(es => ({ ...es, [col]: 'loading' }));
    try {
      const { data } = await api.get(`/${session.session_id}/column-samples`, { params: { column: col, limit: 20 } });
      setExpandedSamples(es => ({ ...es, [col]: data }));
    } catch {
      setExpandedSamples(es => { const n = { ...es }; delete n[col]; return n; });
    }
  };
  const [preflight, setPreflight] = useState(null);
  // Resolve Duplicates step (2.5) — groups from GET /duplicates + per-group choices
  const [dupGroups, setDupGroups] = useState(null);      // {groups, total_groups, shown_groups}
  const [dupChoices, setDupChoices] = useState({});      // key → {master_index, action}
  const [dupResolution, setDupResolution] = useState(null); // merge summary after apply
  const [dupBusy, setDupBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const [destination, setDestination] = useState(pairConfig?.destId || 'generic');
  const [presets, setPresets] = useState([]);
  const [sourceCrm, setSourceCrm] = useState(pairConfig?.sourceId || 'generic');
  const [destCrm, setDestCrm] = useState('');
  // Stripe checkout state
  const [checkingOut, setCheckingOut] = useState(false);
  const [conciergeTier, setConciergeTier] = useState(null);      // tier id while intake modal open
  const [conciergeChecked, setConciergeChecked] = useState({});  // per-tier "Add Concierge" checkbox
  const [checkoutSuccess, setCheckoutSuccess] = useState(null); // {license_key, tier, email}
  // Rewardful affiliate referral id (captured async — sits dormant until you
  // create a "MigrationCrunch Migrations" campaign in the Rewardful dashboard).
  const [referralId, setReferralId] = useState(null);
  // "Will my notes survive?" preview tool — public, no auth, AI-powered
  const [previewNotes, setPreviewNotes] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewResults, setPreviewResults] = useState(null);  // {results: [...], entities_found, notes_analyzed}
  const [previewError, setPreviewError] = useState('');
  // Lazy init: read mc_cancelled from URL synchronously so we don't trip the
  // react-hooks/set-state-in-effect lint rule by setting state inside useEffect.
  const [checkoutCancelled, setCheckoutCancelled] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get('mc_cancelled') === '1';
    } catch { return false; }
  });

  const fileInput = useRef(null);

  // Smooth-scroll to #anchor when the URL hash changes (or on initial mount).
  // React Router's <Link to="/migrate#pricing"> updates the URL but does not
  // scroll the viewport to the target element — this effect fixes that for
  // every hash-based nav link on the page (#pricing, #fit-check, #calculator).
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Defer one tick so the target section has mounted (handles initial load
    // race where the effect fires before the DOM node exists).
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(t);
  }, [location.pathname, location.hash]);

  // Read ?from= URL param on mount + write back on change (SEO-deeplink friendly)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const from = (params.get('from') || '').toLowerCase();
      if (from && SOURCE_CRMS[from]) setSourceCrm(from);
      const to = (params.get('to') || '').toLowerCase();
      const toDest = DEST_CRMS.find(d => d.id === to);
      if (toDest && toDest.id) {
        setDestCrm(toDest.id);
        if (toDest.presetId) setDestination(toDest.presetId);
      }

      // Stripe checkout return — poll license issuance + show success modal
      const mcSession = params.get('mc_session_id');
      const cancelled = params.get('mc_cancelled');
      if (cancelled === '1') {
        // Already captured in lazy-init useState above; just strip the param
        // so a refresh doesn't keep showing the banner.
        params.delete('mc_cancelled');
        const url = new URL(window.location.href);
        url.search = params.toString();
        window.history.replaceState({}, '', url.toString());
      }
      if (mcSession) {
        (async () => {
          // Poll for up to ~30s while Stripe webhook / status reconciles
          for (let i = 0; i < 10; i++) {
            try {
              const { data } = await api.get(`/checkout/status/${mcSession}`);
              if (data.license_issued && data.license_key) {
                setCheckoutSuccess(data);
                if (data.license_key) {
                  setLicenseKey(data.license_key);
                  localStorage.setItem('mc_license', data.license_key);
                }
                // PostHog conversion event — fires exactly once per license.
                // Lets us see "Reddit → clicks → purchases" in the funnel
                // without needing Reddit's own pixel. Guarded so a re-poll
                // hitting cache after refresh doesn't double-fire.
                if (window.posthog && data.license_key) {
                  try {
                    window.posthog.capture('migration_purchase', {
                      license_key: data.license_key,
                      tier: data.tier,
                      amount: data.amount,
                      currency: 'USD',
                    });
                  } catch { /* posthog unavailable — ignore */ }
                }
                // Reddit Pixel Lead event — fires on the success/confirmation
                // moment so Reddit can optimize ad delivery toward buyers.
                // Pixel ID lives in index.html (a2_j9hy8o5ahk4u).
                if (window.rdt && data.license_key) {
                  try {
                    window.rdt('track', 'Lead', {
                      value: data.amount,
                      currency: 'USD',
                      transactionId: data.license_key,
                    });
                  } catch { /* reddit pixel unavailable — ignore */ }
                }
                // Google Ads "Purchase" conversion (AW-18349646774).
                // transaction_id = license key so Google dedupes if the
                // customer refreshes the success page. Value = real tier
                // amount, not the default 1.0.
                if (window.gtag && data.license_key) {
                  try {
                    window.gtag('event', 'conversion', {
                      send_to: 'AW-18349646774/E4_dCP3O8dYcELbH5a1E',
                      value: data.amount || 1.0,
                      currency: 'USD',
                      transaction_id: data.license_key,
                    });
                  } catch { /* gtag unavailable — ignore */ }
                }
                break;
              }
            } catch {
              // intentionally swallow — we retry every 3s
            }
            await new Promise(r => setTimeout(r, 3000));
          }
          // Clean the URL regardless
          const url = new URL(window.location.href);
          url.searchParams.delete('mc_session_id');
          url.searchParams.delete('tier');
          window.history.replaceState({}, '', url.toString());
        })();
      }

      // License auto-load — when a customer clicks the "Open my migration
      // tool →" button in their welcome email (link points at
      // /migrationcrunch?license=MC-XXX-...), we pull the key out of the URL,
      // validate it via /api/migrate-pro/check-license, persist to
      // localStorage so future visits skip this step, and then clean the URL
      // so refresh/share doesn't leak the key.
      const urlLicense = (params.get('license') || '').trim().toUpperCase();
      if (urlLicense && /^MC-[A-Z]{2,3}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(urlLicense)) {
        setLicenseKey(urlLicense);
        localStorage.setItem('mc_license', urlLicense);
        checkLicense(urlLicense);
        const url = new URL(window.location.href);
        url.searchParams.delete('license');
        window.history.replaceState({}, '', url.toString());
      }
    } catch {}
  }, []);

  const updateSourceCrm = (id) => {
    setSourceCrm(id);
    try {
      const url = new URL(window.location.href);
      if (id === 'generic') url.searchParams.delete('from');
      else url.searchParams.set('from', id);
      window.history.replaceState({}, '', url.toString());
    } catch {}
  };

  const updateDestCrm = (id) => {
    setDestCrm(id);
    const d = DEST_CRMS.find(x => x.id === id);
    if (d?.presetId) setDestination(d.presetId);
    try {
      const url = new URL(window.location.href);
      if (!id) url.searchParams.delete('to');
      else url.searchParams.set('to', id);
      window.history.replaceState({}, '', url.toString());
    } catch {}
  };

  // When both hero dropdowns match a dedicated pair landing page, surface it.
  const heroPairSlug = (() => {
    if (pairConfig) return null;
    const srcSeg = PAIR_SOURCE_SEG[sourceCrm];
    const destSeg = DEST_CRMS.find(d => d.id === destCrm)?.pairSeg;
    if (!srcSeg || !destSeg) return null;
    const slug = `${srcSeg}-to-${destSeg}`;
    return MIGRATION_PAIRS[slug] ? slug : null;
  })();

  const reset = () => {
    setStep(0); setSession(null); setMappings({}); setPreflight(null);
    setError(''); setBusy(false); setAudit(null); setAuditBusy(false);
    setDupGroups(null); setDupChoices({}); setDupResolution(null);
  };

  const checkLicense = async (key) => {
    try {
      const { data } = await api.post('/license/check', { license_key: key });
      setLicenseInfo(data);
      if (data.valid) {
        localStorage.setItem('mc_license', key);
        return data;
      }
      return data;
    } catch { return null; }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, {
        params: licenseKey ? { license_key: licenseKey } : {},
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSession(data);
      setLicenseInfo(data.license);
      const initial = {};
      Object.entries(data.ai_mappings || {}).forEach(([col, m]) => initial[col] = m.target || 'skip');
      data.columns.forEach(c => { if (!initial[c]) initial[c] = 'skip'; });
      setMappings(initial);
      // Load presets
      try {
        const { data: ps } = await api.get('/presets');
        setPresets(ps);
      } catch {}
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try a smaller file or different format.');
    } finally { setBusy(false); }
  };

  const runPreflight = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/${session.session_id}/preflight`, { mappings });
      setPreflight(data);
      // Mappings may have changed → any prior duplicate resolution is stale
      setDupGroups(null); setDupChoices({}); setDupResolution(null);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Pre-flight failed');
    } finally { setBusy(false); }
  };

  // ── Resolve Duplicates (step 2.5) ──
  const loadDuplicates = async () => {
    setDupBusy(true); setError('');
    try {
      const { data } = await api.get(`/${session.session_id}/duplicates`);
      setDupGroups(data);
      const init = {};
      (data.groups || []).forEach(g => { init[g.key] = { master_index: g.suggested_master_index, action: 'merge' }; });
      setDupChoices(init);
      setStep(2.5);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load duplicate groups');
    } finally { setDupBusy(false); }
  };

  const postResolutions = async (payload) => {
    setDupBusy(true); setError('');
    try {
      const { data } = await api.post(`/${session.session_id}/resolve-duplicates`, payload);
      setDupResolution(data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Merge failed — please try again');
    } finally { setDupBusy(false); }
  };

  const applyResolutions = () => postResolutions({
    resolutions: Object.entries(dupChoices).map(([key, c]) => ({ key, master_index: c.master_index, action: c.action })),
  });

  // Backend re-derives every group itself — handles files with more groups
  // than the UI shows.
  const autoMergeAll = () => postResolutions({ resolutions: [], auto: true });

  const downloadCsv = async () => {
    setBusy(true); setError('');
    try {
      const resp = await axios.get(`${API_URL}/api/migrate-pro/${session.session_id}/download/${destination}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'migrationcrunch_export.csv';
      a.click();
      URL.revokeObjectURL(url);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Download failed');
    } finally { setBusy(false); }
  };

  const runAudit = async () => {
    if (auditBusy) return;
    setAuditBusy(true); setError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/migrate-pro/${session.session_id}/integrity/${destination}`);
      setAudit(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Audit failed — try again');
    } finally { setAuditBusy(false); }
  };

  // SEO meta tags for the pair-specific landing pages (act-to-hubspot, etc.).
  // Sets document.title, meta description, and Open Graph / Twitter Card
  // tags so each pair URL ranks for its own keywords AND shares with a
  // custom preview card on LinkedIn / X / Slack / Discord / Facebook.
  // For the generic /migrate landing (no pairConfig), use a default
  // MigrationCrunch-specific title so it doesn't inherit the SalesCrunchCRM
  // global title.
  useEffect(() => {
    const prevTitle = document.title;
    const isPair = !!pairConfig;
    const title = isPair
      ? pairConfig.seoTitle
      : 'MigrationCrunch — Migrate any CRM without losing a single note';
    const description = isPair
      ? pairConfig.seoDescription
      : 'Leaving Salesforce or HubSpot? Patent-pending AI keeps every note linked to the right contact. $299 contacts-only or $599 full migration with notes + history. Done in minutes, not weeks.';
    const slug = isPair ? pairConfig.slug : '';
    document.title = title;

    // Helper: upsert a <meta> tag matching the given attribute=value.
    // Returns a cleanup fn that restores the previous content or removes
    // the tag if we created it.
    const upsertMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      let created = false;
      let prev = null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created = true;
      } else {
        prev = el.getAttribute('content');
      }
      el.setAttribute('content', content);
      return () => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute('content', prev);
      };
    };

    const pageUrl = isPair
      ? `${window.location.origin}/migrate/${slug}`
      : `${window.location.origin}/migrate`;
    const ogImage = `${window.location.origin}/og-default.svg`;

    const restorers = [
      upsertMeta('name', 'description', description),
      // Open Graph (LinkedIn, Facebook, Slack, Discord)
      upsertMeta('property', 'og:title', title),
      upsertMeta('property', 'og:description', description),
      upsertMeta('property', 'og:url', pageUrl),
      upsertMeta('property', 'og:type', 'website'),
      upsertMeta('property', 'og:site_name', 'MigrationCrunch'),
      upsertMeta('property', 'og:image', ogImage),
      // Twitter / X
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', title),
      upsertMeta('name', 'twitter:description', description),
      upsertMeta('name', 'twitter:image', ogImage),
    ];

    return () => {
      document.title = prevTitle;
      restorers.forEach(fn => fn());
    };
  }, [pairConfig]);

  // Capture Rewardful referral id once the tracking script is ready (async).
  // Rewardful sets window.Rewardful.referral after an internal API call, so we
  // must subscribe to its 'ready' event AND poll briefly as a fallback. This
  // sits dormant until a MigrationCrunch campaign exists in Rewardful — the
  // referral_id is sent to the backend regardless; without a matching campaign
  // Rewardful simply ignores the conversion (no harm done).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const capture = () => {
      try {
        const r = window.Rewardful && window.Rewardful.referral;
        if (r) setReferralId(r);
      } catch {}
    };
    if (typeof window.rewardful === 'function') {
      try { window.rewardful('ready', capture); } catch {}
    }
    let polls = 0;
    const id = setInterval(() => {
      polls++;
      capture();
      if (polls >= 10) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, []);

  // "Will my notes survive?" lead-magnet preview. Splits the textarea by
  // blank-line OR newline (whichever produces 2-5 chunks), sends to the
  // public /preview-notes endpoint, renders the AI-extracted entities below.
  const runNotePreview = async () => {
    setPreviewBusy(true); setPreviewError(''); setPreviewResults(null);
    try {
      const raw = previewNotes.trim();
      if (!raw) { setPreviewError('Paste at least one note above.'); setPreviewBusy(false); return; }
      // Try blank-line split first; fall back to single-newline split if user
      // didn't double-space their notes.
      let chunks = raw.split(/\n\s*\n+/).map(s => s.trim()).filter(Boolean);
      if (chunks.length < 2) {
        chunks = raw.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
      }
      chunks = chunks.slice(0, 5);  // hard cap matches backend
      const { data } = await api.post('/preview-notes', { notes: chunks });
      setPreviewResults(data);
    } catch (err) {
      setPreviewError(err.response?.data?.detail || 'AI preview failed. Try again.');
    } finally {
      setPreviewBusy(false);
    }
  };

  // Kick off Stripe Checkout for a paid migration tier (one-time payment).
  const startCheckout = async (tier, verifiedEmail = null) => {
    setCheckingOut(true); setError('');
    // PostHog "checkout_started" event — fires the moment they click Buy.
    // Combined with the migration_purchase event on success, this gives us
    // click→purchase conversion by UTM source (Reddit, affiliate, direct, etc.)
    if (window.posthog) {
      try {
        window.posthog.capture('checkout_started', {
          tier,
          source_crm: sourceCrm,
          product: 'migrationcrunch',
        });
      } catch { /* posthog unavailable — ignore */ }
    }
    try {
      // Final synchronous read of window.Rewardful.referral as a fallback in
      // case the async 'ready' event hasn't fired yet (mirrors HomePage flow).
      let finalReferralId = referralId;
      if (!finalReferralId && typeof window !== 'undefined') {
        for (let i = 0; i < 6; i++) {
          const r = window.Rewardful && window.Rewardful.referral;
          if (r) { finalReferralId = r; break; }
          await new Promise(res => setTimeout(res, 250));
        }
      }
      const { data } = await api.post('/checkout/start', {
        tier,
        origin_url: window.location.origin,
        source_crm: sourceCrm,
        referral_id: finalReferralId,  // null when no Rewardful campaign active
        email: verifiedEmail,  // pre-verified via EmailGateModal; Stripe locks the field
      });
      if (data?.url) {
        window.location.href = data.url;  // jump to Stripe
      } else {
        setError('Could not start checkout. Please try again.');
        setCheckingOut(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Checkout could not be started. Please try again or email chip@migrationcrunch.com.');
      setCheckingOut(false);
    }
  };

  // ─── Email-gate flow ──────────────────────────────────────────────
  // pendingTier holds the tier the customer just clicked. When set, the
  // EmailGateModal is open and asking for double-confirmed email entry.
  // On confirm we forward to startCheckout(tier, verifiedEmail). Idea credit:
  // Cynthia Tippmann (2026-06-20) — prevents typo'd-license-email scenarios.
  const [pendingTier, setPendingTier] = useState(null);
  const startConciergeCheckout = async (form) => {
    setCheckingOut(true);
    try {
      const referral = (typeof window !== 'undefined' && window.Rewardful && window.Rewardful.referral) || null;
      const { data } = await api.post('/checkout/start', {
        tier: conciergeTier,
        concierge: true,
        origin_url: window.location.origin,
        email: form.email,
        source_crm: form.sourceCrm,
        dest_crm: form.destCrm,
        phone: form.phone,
        call_window: form.callWindow,
        db_size: form.dbSize,
        referral_id: referral,
      });
      window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not start checkout — try again or email chip@migrationcrunch.com');
      setCheckingOut(false);
      setConciergeTier(null);
    }
  };

  const requestCheckout = (tier) => {
    if (tier === 'free') {
      // Free tier has no Stripe step; nothing to gate. Go direct.
      return startCheckout('free');
    }
    if (conciergeChecked[tier] && CONCIERGE_ADDONS[tier]) {
      // Concierge add-on checked → intake modal collects email + phone +
      // call window, then one combined Stripe payment.
      setConciergeTier(tier);
      return;
    }
    setPendingTier(tier);
  };
  const handleEmailGateConfirm = (verifiedEmail) => {
    const tier = pendingTier;
    setPendingTier(null);
    if (tier) startCheckout(tier, verifiedEmail);
  };
  const handleEmailGateCancel = () => setPendingTier(null);

  const TIER_LABELS = {
    contacts_only: 'Contacts Only · $299',
    starter_pro: 'Starter Pro · $599',
    growth_ai: 'Growth AI · $1,799',
    enterprise_ai: 'Enterprise AI · $4,499',
    unlimited_ai: 'Unlimited AI · $7,999',
  };

  // ─── LANDING (step 0) ─────────────────────────────────────────
  if (step === 0) {
    return (
      <div className="min-h-screen bg-[#0a0e27] text-white" data-testid="mc-landing">
        {/* Top bar */}
        <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0F0F1A]/95 backdrop-blur-sm z-30">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <Link to="/migrate" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="mc-nav-logo">
              <Database size={28} weight="fill" className="text-[#F39C12]" />
              <span className="text-xl font-black">Migration<span className="text-[#F39C12]">Crunch</span></span>
            </Link>
            {/* Desktop nav — hidden on mobile to avoid crowding */}
            <nav className="hidden md:flex items-center gap-6 text-sm">
              <Link to="/migrate/walkthrough" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-how-it-works">
                How it works
              </Link>
              <Link to="/migrate#calculator" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-calculator">
                Savings calculator
              </Link>
              <Link to="/migrate#fit-check" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-fit">
                Fit check
              </Link>
              <Link to="/migrate/compare" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-compare">
                Compare vendors
              </Link>
              <Link to="/migrate/gemini" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-gemini">
                What Gemini said
              </Link>
              <Link to="/migrate/concierge" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-concierge">
                Concierge
              </Link>
              <Link to="/migrate#pricing" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-pricing">
                Pricing
              </Link>
              <a href="/" className="text-sm text-white/70 hover:text-white border border-white/20 hover:border-white/40 rounded px-3 py-1.5 transition-colors" data-testid="mc-nav-try-crm">
                Try SalesCrunchCRM →
              </a>
            </nav>
            {/* Mobile compact nav — just the CRM CTA */}
            <a href="/" className="md:hidden text-xs text-white/70 hover:text-white border border-white/20 rounded px-2 py-1 transition-colors" data-testid="mc-nav-try-crm-mobile">
              CRM →
            </a>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 py-16 text-center max-w-4xl mx-auto">
          {pairConfig ? (
            <div className="inline-flex flex-wrap items-center justify-center gap-2 mb-6" data-testid="mc-pair-badge">
              <span className="bg-white/10 border border-white/20 px-3 py-1.5 rounded text-sm font-bold text-white/90">
                {pairConfig.sourceName}
              </span>
              <ArrowRight size={16} weight="bold" className="text-[#F39C12]" />
              <span className="bg-[#F39C12]/15 border border-[#F39C12]/50 px-3 py-1.5 rounded text-sm font-bold text-[#F39C12]">
                {pairConfig.destName}
              </span>
              <span className="text-white/40 text-xs ml-2">in {pairConfig.estimatedTime}</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
              <span className="text-white/60 text-sm">I'm coming from</span>
              <select
                value={sourceCrm}
                onChange={(e) => updateSourceCrm(e.target.value)}
                data-testid="mc-source-crm-select"
                className="bg-white/10 hover:bg-white/15 border border-[#F39C12]/40 hover:border-[#F39C12] text-white text-sm font-bold px-3 py-1.5 rounded outline-none cursor-pointer transition-colors"
              >
                {Object.entries(SOURCE_CRMS).map(([id, cfg]) => (
                  <option key={id} value={id} className="bg-[#0a0e27]">{cfg.label}</option>
                ))}
              </select>
              <span className="text-white/60 text-sm">moving to</span>
              <select
                value={destCrm}
                onChange={(e) => updateDestCrm(e.target.value)}
                data-testid="mc-dest-crm-select"
                className="bg-white/10 hover:bg-white/15 border border-emerald-400/40 hover:border-emerald-400 text-white text-sm font-bold px-3 py-1.5 rounded outline-none cursor-pointer transition-colors"
              >
                {DEST_CRMS.map((d) => (
                  <option key={d.id} value={d.id} className="bg-[#0a0e27]">{d.label}</option>
                ))}
              </select>
            </div>
          )}
          {heroPairSlug && (
            <div className="-mt-2 mb-6">
              <Link
                to={`/migrate/${heroPairSlug}`}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-300 hover:text-emerald-200 underline underline-offset-4 decoration-emerald-400/40 transition-colors"
                data-testid="mc-hero-pair-guide-link"
              >
                Read our dedicated {MIGRATION_PAIRS[heroPairSlug].sourceName} → {MIGRATION_PAIRS[heroPairSlug].destName} playbook
                <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          )}
          <div className="inline-flex items-center gap-2 flex-wrap justify-center mb-6">
            <span className="inline-flex items-center gap-2 bg-[#F39C12]/10 border border-[#F39C12]/30 px-3 py-1 rounded-full text-[12px]">
              <Sparkle size={12} weight="fill" className="text-[#F39C12]" />
              <span className="text-[#F39C12]">Powered by AI · Saves 3+ weeks</span>
            </span>
            <span
              className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-400/30 px-3 py-1 rounded-full text-[12px] text-emerald-300"
              data-testid="mc-patent-pending"
              title="Provisional patent application filed with the USPTO"
            >
              <Shield size={12} weight="fill" />
              Patent Pending
            </span>
            <span
              className="inline-flex items-center gap-1.5 bg-blue-500/10 border border-blue-400/30 px-3 py-1 rounded-full text-[12px] text-blue-300"
              data-testid="mc-audit-badge"
              title="Every migration ends with a free AI Data Integrity Audit + downloadable certificate"
            >
              <CheckCircle size={12} weight="fill" />
              Free AI Integrity Audit
            </span>
          </div>
          <p className="text-[#F39C12] text-sm font-bold uppercase tracking-widest mb-3" data-testid="mc-tagline">
            What others lose, we preserve.
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6" data-testid="mc-hero-headline">
            {pairConfig ? pairConfig.headline : SOURCE_CRMS[sourceCrm].headline}
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-6 leading-relaxed" data-testid="mc-hero-subhead">
            {pairConfig ? pairConfig.subhead : SOURCE_CRMS[sourceCrm].subhead}
          </p>
          {/* Free CTA lives directly under the subhead so it's above the fold */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-2">
            <button
              onClick={() => fileInput.current?.click()}
              className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-8 py-4 rounded text-base flex items-center gap-2 transition-colors"
              data-testid="mc-cta-primary"
              disabled={busy}
            >
              <UploadSimple size={20} weight="bold" />
              {busy ? 'Analyzing...' : 'Try it free → first 50 contacts + notes'}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,.dbf,.pst"
              className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])}
              data-testid="mc-file-input"
            />
          </div>
          <p className="text-xs text-white/50 mb-8">No signup. No credit card. Drop a file, watch the AI map it in 60 seconds.</p>
          {pairConfig && (
            <>
              <div className="max-w-2xl mx-auto mb-4 grid md:grid-cols-2 gap-3 text-left" data-testid="mc-pair-edges">
                <div className="bg-red-500/5 border border-red-500/20 rounded p-3">
                  <div className="text-[10px] uppercase tracking-wider text-red-300 font-black mb-1">The risk</div>
                  <p className="text-xs text-white/70 leading-relaxed">{pairConfig.painPoint}</p>
                </div>
                <div className="bg-emerald-500/5 border border-emerald-500/30 rounded p-3">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-black mb-1">Our edge</div>
                  <p className="text-xs text-white/80 leading-relaxed">{pairConfig.ourEdge}</p>
                </div>
              </div>
              {/* Confident-subtle nudge: every pair page tells the visitor
                  there's a free alternative. Most will still pick their
                  intended destination, but the few who don't were going
                  to churn anyway — better to capture them as $30/mo subs. */}
              <p
                className="text-xs text-white/40 text-center max-w-2xl mx-auto mb-3"
                data-testid="mc-pair-salescrunch-nudge"
              >
                Or skip the move entirely — <Link
                  to="/pricing"
                  className="text-emerald-300 hover:text-emerald-200 underline"
                >SalesCrunchCRM</Link> is the ACT! you remember, modernized. Migration is free.
              </p>
              {pairConfig.sourceId === 'salesforce' && (
                <p className="text-xs text-center max-w-2xl mx-auto mb-8">
                  <Link
                    to="/migrate/salesforce-true-cost"
                    className="text-amber-300 hover:text-amber-200 underline underline-offset-2 font-bold"
                    data-testid="mc-pair-sf-cost-link"
                  >
                    CFO math: what 3 more years of Salesforce actually costs →
                  </Link>
                </p>
              )}
            </>
          )}
          <p className="text-xs text-white/50 mt-2">
            Want to see what happens after &quot;upload&quot;?{' '}
            <Link
              to="/migrate/walkthrough"
              className="text-amber-300 hover:text-amber-200 underline font-bold"
              data-testid="mc-hero-walkthrough-link"
            >
              See the full 9-step walkthrough →
            </Link>
          </p>
          {error && <div className="mt-4 max-w-md mx-auto bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 text-sm">{error}</div>}
        </section>

        {/* Step 0 — click-by-click export guide, adapts to source CRM */}
        <ExportGuideSection sourceId={pairConfig ? pairConfig.sourceId : sourceCrm} />

        {/* 90-second demo video — produced by VideoCrunch */}
        <section className="px-6 py-16 border-t border-white/10" data-testid="mc-demo-video-section">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#F39C12] text-xs font-black uppercase tracking-[0.2em] text-center mb-2">90-second demo</p>
            <h2 className="text-3xl font-black text-center mb-4">Watch a real 8,500-contact migration</h2>
            <p className="text-sm text-white/70 max-w-2xl mx-auto text-center leading-relaxed mb-8" data-testid="mc-demo-video-intro">
              A messy financial-advisor export goes in. The AI reads the notes, maps every field,
              flags the duplicates, and a clean file comes out — free when your destination is SalesCrunchCRM.
            </p>
            <McDemoVideo />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'VideoObject',
              name: 'MigrationCrunch — real 8,500-contact CRM migration in 90 seconds',
              description: 'Watch MigrationCrunch migrate a real 8,500-contact file: AI note extraction, automatic field mapping, duplicate detection, and a free download when the destination is SalesCrunchCRM.',
              contentUrl: 'https://salescrunchcrm.com/migrationcrunch-demo.mp4',
              thumbnailUrl: 'https://salescrunchcrm.com/migrationcrunch-demo-poster.jpg',
              duration: 'PT1M38S',
              uploadDate: '2026-06-14T08:00:00+00:00',
              publisher: { '@type': 'Organization', name: 'MigrationCrunch' },
            }) }} />
          </div>
        </section>

        {/* Vocabulary translation — pair pages only. Shows exactly how the
            source CRM's object names map to the destination's, building
            trust before the visitor ever uploads a file. */}
        {pairConfig && (() => {
          const vocab = getVocabMap(pairConfig);
          if (!vocab) return null;
          return (
            <section className="px-6 py-16 border-t border-white/10 bg-white/[0.02]" data-testid="mc-vocab-section">
              <div className="max-w-4xl mx-auto">
                <p className="text-[#F39C12] text-xs font-black uppercase tracking-[0.2em] text-center mb-2">Vocabulary translation</p>
                <h2 className="text-3xl font-black text-center mb-4">
                  {pairConfig.sourceName} words → <span className="text-emerald-400">{pairConfig.destName} words</span>
                </h2>
                <p className="text-sm text-white/70 max-w-2xl mx-auto text-center leading-relaxed mb-8" data-testid="mc-vocab-intro">
                  {vocab.intro}
                </p>
                <div className="bg-white/5 border border-white/15 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-[10px] uppercase tracking-widest font-black text-white/40 px-4 py-2.5 border-b border-white/10 bg-black/20">
                    <span>In {pairConfig.sourceName}</span>
                    <span className="w-[14px]" />
                    <span className="text-right">In {pairConfig.destName}</span>
                  </div>
                  {vocab.rows.map((r, i) => (
                    <div
                      key={r.concept}
                      className={`grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-white/5' : ''}`}
                      data-testid={`mc-vocab-row-${i}`}
                    >
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/35 font-bold">{r.concept}</div>
                        <div className="text-sm font-bold text-white/90">{r.from}</div>
                      </div>
                      <ArrowRight size={14} weight="bold" className="text-[#F39C12]" />
                      <div className="text-sm font-bold text-emerald-300 text-right">{r.to}</div>
                    </div>
                  ))}
                </div>
                {vocab.extra && (
                  <div className="mt-4 bg-[#F39C12]/5 border border-[#F39C12]/25 rounded p-4" data-testid="mc-vocab-extra">
                    <div className="text-[10px] uppercase tracking-wider text-[#F39C12] font-black mb-1">Worth knowing</div>
                    <p className="text-xs text-white/75 leading-relaxed">{vocab.extra}</p>
                  </div>
                )}
              </div>
            </section>
          );
        })()}

        {/* The pain */}
        <section className="bg-white/5 px-6 py-16 border-y border-white/10">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-black text-center mb-4">CRM migration is supposed to be hard.</h2>
            <p className="text-center text-white/70 max-w-2xl mx-auto mb-12">
              For 25 years, the same story: export your old database, lose half your data, hand-paste
              every note for three weeks. We fixed it.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-950/30 border border-red-500/20 p-6 rounded">
                <h3 className="font-bold text-lg mb-3 text-red-300">😩 The old way</h3>
                <ul className="space-y-2 text-sm text-white/70">
                  <li>• Export to CSV — half your fields don't map</li>
                  <li>• Notes either don't come, or attach to the wrong contact</li>
                  <li>• Pay a "consultant" $1,500-$5,000</li>
                  <li>• Spend 3 weeks copy-pasting</li>
                  <li>• Give up and lose 20 years of history</li>
                </ul>
              </div>
              <div className="bg-green-950/30 border border-green-500/20 p-6 rounded">
                <h3 className="font-bold text-lg mb-3 text-green-300">✨ MigrationCrunch</h3>
                <ul className="space-y-2 text-sm text-white/70">
                  <li>• Upload any CRM export, AI maps fields in 5 seconds</li>
                  <li>• AI matches notes to the right contact by name/company</li>
                  <li>• <strong className="text-white">From $599 one-time</strong> — not $1,500-$5,000</li>
                  <li>• 10 minutes start to finish</li>
                  <li>• Keep all your relationship history</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="px-6 py-16 max-w-5xl mx-auto">
          <h2 className="text-3xl font-black text-center mb-12">Smart. Fast. Affordable.</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <Sparkle size={32} weight="fill" className="text-[#F39C12] mb-3" />
              <h3 className="font-bold text-lg mb-2">AI Field Mapper</h3>
              <p className="text-sm text-white/60">Even Portuguese, German, or wonky column names — our AI figures out what's a phone, an email, an address. Confidence scores for every guess.</p>
            </div>
            <div>
              <Database size={32} weight="fill" className="text-[#F39C12] mb-3" />
              <h3 className="font-bold text-lg mb-2">Note Matcher</h3>
              <p className="text-sm text-white/60">Got a separate notes file? AI reads each note, finds the name/company inside, attaches it to the right contact. The thing nobody else does.</p>
            </div>
            <div>
              <Lightning size={32} weight="fill" className="text-[#F39C12] mb-3" />
              <h3 className="font-bold text-lg mb-2">Pre-flight Check</h3>
              <p className="text-sm text-white/60">Find duplicates, malformed emails, missing data before you import. No surprises.</p>
            </div>
          </div>
        </section>

        {/* "Will my notes survive?" — lead-magnet preview tool. The AI extracts
            entities the visitor's own notes so they see the moat in action
            BEFORE pricing. Highest-converting demo pattern for AI products. */}
        <section
          className="px-6 py-16 bg-gradient-to-b from-[#0a0e27] via-[#0d1230] to-[#0a0e27] border-t border-white/10"
          id="preview"
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-emerald-300 text-xs font-black uppercase tracking-[0.2em] mb-2">Free · No signup · 30 seconds</p>
              <h2 className="text-3xl md:text-4xl font-black mb-3" data-testid="mc-preview-headline">
                Will your notes <span className="text-emerald-400">survive</span> the migration?
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-sm">
                Paste 2–5 of your real CRM notes below. Our AI shows you exactly what it would
                extract — the names, companies, emails, and phone numbers buried inside the prose.
                This is the data competitors lose. See for yourself before you pay anyone a dime.
              </p>
            </div>

            <div className="bg-white/5 border border-white/15 rounded-lg p-5 md:p-6">
              <div className="flex items-center justify-between gap-3 mb-2">
                <label className="block text-xs uppercase tracking-wider text-white/50 font-bold">
                  Paste your notes (one per blank line)
                </label>
                <button
                  onClick={() => setPreviewNotes(SAMPLE_PREVIEW_NOTES)}
                  className="text-[11px] font-bold text-emerald-300 hover:text-emerald-200 underline underline-offset-2 decoration-emerald-400/40 transition-colors whitespace-nowrap"
                  data-testid="mc-preview-load-sample"
                >
                  No notes handy? Try a sample →
                </button>
              </div>
              <textarea
                value={previewNotes}
                onChange={(e) => setPreviewNotes(e.target.value)}
                placeholder={SAMPLE_PREVIEW_NOTES}
                rows={6}
                maxLength={3500}
                className="w-full bg-black/30 border border-white/20 rounded px-3 py-2 text-sm font-mono text-white/90 placeholder:text-white/30 focus:border-emerald-400 focus:outline-none resize-y"
                data-testid="mc-preview-textarea"
              />
              <div className="flex items-center justify-between mt-3 gap-3">
                <p className="text-[11px] text-white/40">Max 5 notes · Privacy: notes are NOT stored, only entity counts.</p>
                <button
                  onClick={runNotePreview}
                  disabled={previewBusy || !previewNotes.trim()}
                  className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-black px-5 py-2 text-xs font-black rounded transition-colors flex items-center gap-2"
                  data-testid="mc-preview-submit"
                >
                  {previewBusy ? (
                    <><Sparkle size={14} weight="fill" className="animate-pulse" /> Analyzing…</>
                  ) : (
                    <><Sparkle size={14} weight="fill" /> Show me what survives →</>
                  )}
                </button>
              </div>

              {previewError && (
                <div className="mt-4 bg-red-500/10 border border-red-500/30 text-red-200 px-3 py-2 text-xs rounded" data-testid="mc-preview-error">
                  {previewError}
                </div>
              )}

              {previewResults && previewResults.results?.length > 0 && (
                <div className="mt-6 space-y-4" data-testid="mc-preview-results">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-3">
                    <div className="text-xs text-white/60">
                      <span className="text-emerald-300 font-black text-lg">{previewResults.entities_found}</span> entities found across <strong>{previewResults.notes_analyzed}</strong> notes
                    </div>
                    <div className="text-[10px] text-white/40">Each entity becomes a re-linked relationship on the other side.</div>
                  </div>

                  {previewResults.results.map((r, idx) => (
                    <div key={idx} className="grid md:grid-cols-2 gap-3" data-testid={`mc-preview-row-${idx}`}>
                      {/* BEFORE — competitor */}
                      <div className="bg-red-500/5 border border-red-500/20 rounded p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-red-500/20 text-red-300 text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-wider">Competitor</span>
                          <span className="text-[10px] text-white/40">Moves text, loses relationships</span>
                        </div>
                        <p className="text-xs text-white/70 leading-relaxed italic">"{r.original}"</p>
                        <div className="mt-3 text-[10px] text-red-300/70 border-t border-red-500/15 pt-2">
                          ⚠ Text-only blob. Names mentioned are NOT linked to their contact records.
                        </div>
                      </div>

                      {/* AFTER — us */}
                      <div className="bg-emerald-500/5 border border-emerald-500/30 rounded p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-emerald-500/20 text-emerald-300 text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-wider">MigrationCrunch</span>
                          <span className="text-[10px] text-emerald-300/80">{r.summary}</span>
                        </div>
                        <div className="space-y-1.5">
                          {r.people?.length > 0 && (
                            <div className="text-[11px] text-white/80">
                              <span className="text-white/40 mr-1.5">People:</span>
                              {r.people.map((p, i) => (
                                <span key={i} className="inline-block bg-emerald-400/15 text-emerald-200 px-1.5 py-0.5 rounded mr-1 font-mono">{p}</span>
                              ))}
                            </div>
                          )}
                          {r.companies?.length > 0 && (
                            <div className="text-[11px] text-white/80">
                              <span className="text-white/40 mr-1.5">Companies:</span>
                              {r.companies.map((c, i) => (
                                <span key={i} className="inline-block bg-blue-400/15 text-blue-200 px-1.5 py-0.5 rounded mr-1 font-mono">{c}</span>
                              ))}
                            </div>
                          )}
                          {r.emails?.length > 0 && (
                            <div className="text-[11px] text-white/80">
                              <span className="text-white/40 mr-1.5">Emails:</span>
                              {r.emails.map((e, i) => (
                                <span key={i} className="inline-block bg-amber-400/15 text-amber-200 px-1.5 py-0.5 rounded mr-1 font-mono">{e}</span>
                              ))}
                            </div>
                          )}
                          {r.phones?.length > 0 && (
                            <div className="text-[11px] text-white/80">
                              <span className="text-white/40 mr-1.5">Phones:</span>
                              {r.phones.map((p, i) => (
                                <span key={i} className="inline-block bg-purple-400/15 text-purple-200 px-1.5 py-0.5 rounded mr-1 font-mono">{p}</span>
                              ))}
                            </div>
                          )}
                          {r.references_other_contact && (
                            <div className="text-[10px] text-emerald-300 mt-2 border-t border-emerald-500/15 pt-2">
                              🔗 References another contact → we re-link this on the other side.
                            </div>
                          )}
                          {!r.people?.length && !r.companies?.length && !r.emails?.length && !r.phones?.length && (
                            <div className="text-[10px] text-white/40 italic">No structured data found in this note — both tools would treat it the same.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="bg-[#F39C12]/10 border border-[#F39C12]/30 rounded p-4 mt-6 text-center">
                    <p className="text-sm text-white/90 mb-3">
                      Convinced? <strong className="text-[#F39C12]">Now do it for all of your notes.</strong>
                    </p>
                    <button
                      onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                      className="bg-[#F39C12] hover:bg-[#E08C00] text-black px-5 py-2 text-xs font-black rounded transition-colors"
                      data-testid="mc-preview-to-pricing"
                    >
                      See pricing →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Competitor comparison snippet — visible ABOVE the fit-check
            because most keyword-ad traffic arrives with a competitor in
            mind ("import2 alternative" etc.). The full comparisons live
            at /vs-import2 and /vs-migratemycrm; this snippet is the
            "why we're worth clicking" preview. */}
        <section className="px-6 py-14 border-y border-white/10 bg-white/[0.02]" data-testid="mc-competitor-snippet">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <div className="text-[10px] uppercase tracking-widest font-black text-amber-400 mb-2">
                Comparing us to Import2 or MigrateMyCRM?
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-white mb-3">
                Same job. Simpler pricing. Faster finish.
              </h2>
              <p className="text-sm text-white/60 max-w-2xl mx-auto">
                Both charge by &ldquo;records&rdquo; — every note and email counts
                separately. Read the plain-English breakdown before you
                pay anything.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <a
                href="/vs-import2"
                className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-400/50 rounded-lg p-5 transition-all"
                data-testid="mc-snippet-vs-import2"
              >
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-lg font-black text-white">MigrationCrunch vs. Import2</div>
                  <ArrowRight size={16} weight="bold" className="text-amber-400 group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="text-sm text-white/70 leading-relaxed">
                  <strong className="text-white">1,000 contacts + history → ~15,000 records → $499-$999.</strong>{' '}
                  Ours: <strong className="text-amber-400">$599 flat</strong>. Every note stays linked to the right contact.
                </p>
              </a>
              <a
                href="/vs-migratemycrm"
                className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-400/50 rounded-lg p-5 transition-all"
                data-testid="mc-snippet-vs-migratemycrm"
              >
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-lg font-black text-white">MigrationCrunch vs. MigrateMyCRM</div>
                  <ArrowRight size={16} weight="bold" className="text-amber-400 group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="text-sm text-white/70 leading-relaxed">
                  <strong className="text-white">Their $349 starter = &lt; 12,500 RECORDS (~800 contacts).</strong>{' '}
                  Their guided add-on: <strong className="text-white">+$875, 1-2 weeks.</strong>{' '}
                  Ours: <strong className="text-amber-400">$299 contacts-only or $599 with notes — flat, minutes.</strong>
                </p>
              </a>
            </div>
          </div>
        </section>

        {/* "Is MigrationCrunch right for me?" wizard — protects refund rate
            by honestly steering complex enterprise migrations to the right
            tier (or to a human consultant) BEFORE they buy.
            Anchored at /migrate#fit-check for the top-nav link. */}
        <section id="fit-check" className="scroll-mt-24">
        <MigrationFitWizard
          onSelectTier={(tierId) => {
            // Scroll to pricing first; the existing tier-card CTAs handle the
            // actual Stripe checkout. If a specific tier was recommended, we
            // also highlight it briefly via querySelector.
            const pricing = document.getElementById('pricing');
            if (pricing) pricing.scrollIntoView({ behavior: 'smooth' });
            // Optional UX nicety: brief outline pulse on the recommended card.
            // Tier cards use hyphenated testids (mc-tier-starter-pro), but the
            // wizard sends underscored ids (starter_pro) — convert.
            setTimeout(() => {
              const slug = tierId.replace(/_/g, '-');
              const card = document.querySelector(`[data-testid="mc-tier-${slug}"]`);
              if (card) {
                card.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'ring-offset-[#0F0F1A]');
                setTimeout(() => card.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2', 'ring-offset-[#0F0F1A]'), 3500);
              }
            }, 600);
          }}
        />
        </section>

        {/* "What only we can do" teaser → deep link to /migrate/compare.
            Punchy 4-bullet pitch surfacing the competitive moat without
            burying the main /migrate page in a 2000-word comparison. The
            full comparison lives on its own SEO landing page. */}
        {!pairConfig && (
          <section className="border-t border-white/10 px-6 py-14 bg-gradient-to-b from-emerald-500/[0.03] to-transparent" data-testid="mc-only-we-do">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-emerald-300 uppercase mb-2">
                  <Sparkle size={12} weight="fill" /> The unfair advantage
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-3">
                  Five things <span className="text-emerald-300">only we do.</span>
                </h2>
                <p className="text-white/60 text-sm md:text-base max-w-2xl mx-auto">
                  Every other migration tool maps fields to fields. We extract names,
                  phones, emails, and relationships buried <em className="text-white">inside</em> your typed notes.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid="mc-owd-notes">
                  <div className="text-[10px] font-bold text-amber-300 uppercase tracking-widest mb-2">Notes → Fields</div>
                  <p className="text-sm text-white/80 leading-relaxed">
                    <strong>Patent-pending AI</strong> recovers names, phones, emails, and relationships that live inside your 20-year-old typed notes — and lands them in their proper destination fields. Competitors leave that data as text blobs and lose it forever. We don&apos;t.
                  </p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid="mc-owd-speed">
                  <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Minutes, not weeks</div>
                  <p className="text-sm text-white/80 leading-relaxed">
                    100,000 contacts in under 10 minutes. The mid-market &quot;done-for-you&quot;
                    competitors quote 2–6 weeks because humans hand-clean the notes
                    problem we automate.
                  </p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid="mc-owd-price">
                  <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Complexity ≠ price</div>
                  <p className="text-sm text-white/80 leading-relaxed">
                    Everyone else charges more for &quot;messy data.&quot; We don&apos;t. $599
                    handles a clean 1,000-contact Salesforce export <em>and</em> a
                    tangled 20-year mess of notes — same price. AI is doing the
                    work, not a billable human.
                  </p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid="mc-owd-honesty">
                  <div className="text-[10px] font-bold text-purple-300 uppercase tracking-widest mb-2">We&apos;ll tell you when we&apos;re NOT the fit</div>
                  <p className="text-sm text-white/80 leading-relaxed">
                    Run our 30-second fit check above. If your shop runs on custom
                    Salesforce workflows, we&apos;ll tell you to hire a human consultant
                    instead. Nobody else says that out loud.
                  </p>
                </div>
                <div className="bg-blue-500/[0.06] border border-blue-400/30 rounded-lg p-5 md:col-span-2" data-testid="mc-owd-audit">
                  <div className="text-[10px] font-bold text-blue-300 uppercase tracking-widest mb-2">🔍 Built-in AI Auditor — the &quot;hidden nightmare&quot; killer</div>
                  <p className="text-sm text-white/80 leading-relaxed">
                    Everyone talks about the data transfer like it&apos;s the hard part. It&apos;s not.
                    The real nightmare starts <em>after</em> go-live: weeks stranded in Excel trying to
                    prove your records, notes, and relationships actually moved correctly. MigrationCrunch
                    is the only migration tool that <strong>double-checks its own work</strong> — Crunchy
                    re-derives every exported record from your source file, cross-verifies all of it
                    (including cryptographic round-trip validation of every note payload), and hands you a
                    free, downloadable <strong>Data Integrity Certificate</strong>: &quot;We checked 50,000
                    records — 49,982 verified, here are the 18 to look at.&quot; No Excel. No guessing. Included
                    free with every migration, even the free trial.
                  </p>
                </div>
              </div>
              <div className="text-center">
                <Link
                  to="/migrate/compare"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-emerald-400/60 hover:border-emerald-300 hover:bg-emerald-400/10 text-emerald-300 font-bold rounded-md transition-colors"
                  data-testid="mc-owd-see-comparison"
                >
                  See the full vendor comparison <ArrowRight size={14} weight="bold" />
                </Link>
                <p className="text-[11px] text-white/40 mt-3">
                  MigrationCrunch vs MigrateMyCRM, Import2, Trujay, and enterprise agencies
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Savings calculator — interactive widget showing agency vs DIY vs MC
            costs for any contact count. Anchored at /migrate#calculator. */}
        <MigrationSavingsCalculator />

        {/* Pricing — 4 paid tiers + free trial. Priced for AI Note Re-Association moat. */}
        <section className="bg-white/5 border-t border-white/10 px-6 py-16 scroll-mt-24" id="pricing">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-3">
              <p className="text-[#F39C12] text-xs font-black uppercase tracking-[0.2em] mb-2">Patent-Pending AI Note Re-Association</p>
              <h2 className="text-3xl md:text-4xl font-black mb-3" data-testid="mc-pricing-headline">
                Move the meaning, <span className="text-[#F39C12]">not just the text.</span>
              </h2>
            </div>
            <p className="text-center text-white/60 max-w-3xl mx-auto mb-3 text-sm">
              Competitors copy your note text but break the links between contacts and deals.
              We re-build the relationships <em>inside</em> your notes — so 20 years of sales history
              stays connected to the right people on the other side.
            </p>
            <p className="text-center text-[11px] text-white/40 max-w-3xl mx-auto mb-12">
              All tiers · One-time payment · No subscriptions · Minutes, not weeks · Secure Stripe checkout · Free AI Data Integrity Audit + Certificate on every migration
            </p>

            {checkoutCancelled && (
              <div className="max-w-2xl mx-auto mb-8 bg-amber-500/10 border border-amber-500/30 text-amber-200 px-4 py-2 text-sm rounded text-center" data-testid="mc-checkout-cancelled">
                Checkout cancelled. No charge was made — pick a tier whenever you're ready.
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {/* Free trial */}
              <div className="bg-white/5 border border-white/20 p-5 rounded flex flex-col" data-testid="mc-tier-free">
                <div className="text-xs text-white/60 mb-1 uppercase tracking-wider">Free Trial</div>
                <div className="text-3xl font-black mb-1">$0</div>
                <div className="text-xs text-white/40 mb-4">≤ 50 contacts + notes</div>
                <ul className="text-xs text-white/70 space-y-1.5 mb-5 flex-1">
                  <li>✓ AI field mapping</li>
                  <li>✓ Pre-flight check</li>
                  <li>✓ Generic CSV download</li>
                  <li>✓ AI Integrity Audit + Certificate</li>
                  <li className="text-white/40">— No AI note recovery</li>
                </ul>
                <button
                  onClick={() => fileInput.current?.click()}
                  className="w-full bg-white/10 hover:bg-white/20 py-2 text-xs font-bold rounded transition-colors"
                  data-testid="mc-tier-free-cta"
                >
                  Try free →
                </button>
              </div>

              {/* Contacts Only $299 — value tier for buyers who don't need
                  notes/history. Positioned right before Starter Pro so the
                  $299 → $599 upsell is visually obvious. Undercuts
                  MigrateMyCRM's $349 starter and Import2's $499 base. */}
              <div className="bg-white/5 border border-white/20 p-5 rounded flex flex-col" data-testid="mc-tier-contacts-only">
                <div className="text-xs text-white/60 mb-1 uppercase tracking-wider">Contacts Only</div>
                <div className="text-3xl font-black mb-1">$299</div>
                <div className="text-xs text-white/40 mb-4">≤ 12,500 contacts</div>
                <ul className="text-xs text-white/70 space-y-1.5 mb-5 flex-1">
                  <li>✓ Contacts + custom fields</li>
                  <li>✓ All 12 destination CRMs</li>
                  <li>✓ 30-day license</li>
                  <li className="text-white/40">— No notes / activity history</li>
                  <li className="text-white/40">— No AI note re-association</li>
                </ul>
                <ConciergeAddonCheckbox
                  tier="contacts_only"
                  price={CONCIERGE_ADDONS.contacts_only}
                  checked={!!conciergeChecked.contacts_only}
                  onChange={(v) => setConciergeChecked(c => ({ ...c, contacts_only: v }))}
                />
                <button
                  onClick={() => requestCheckout('contacts_only')}
                  disabled={checkingOut}
                  className="w-full bg-white/10 hover:bg-white/20 disabled:opacity-60 py-2 text-xs font-bold rounded transition-colors"
                  data-testid="mc-tier-contacts-only-cta"
                >
                  {checkingOut ? 'Opening Stripe…' : 'Choose Contacts Only →'}
                </button>
                <p className="text-[10px] text-white/40 mt-2 text-center">MigrateMyCRM: $349 · Import2: $499 base</p>
              </div>

              {/* Starter Pro $599 — small business, MOST POPULAR */}
              <div className="bg-[#F39C12]/10 border-2 border-[#F39C12] p-5 rounded relative flex flex-col" data-testid="mc-tier-starter-pro">
                <div className="absolute top-0 right-4 -translate-y-1/2 bg-[#F39C12] text-black text-[10px] font-black px-2 py-0.5 rounded">MOST POPULAR</div>
                <div className="text-xs text-[#F39C12] mb-1 uppercase tracking-wider">Starter Pro</div>
                <div className="text-3xl font-black mb-1">$599</div>
                <div className="text-xs text-white/40 mb-4">≤ 12,500 contacts + notes</div>
                <ul className="text-xs text-white/80 space-y-1.5 mb-5 flex-1">
                  <li>✓ Everything in Contacts Only</li>
                  <li>✓ <strong>Notes + activity history</strong></li>
                  <li>✓ <strong>AI Note Re-Association</strong></li>
                  <li>✓ Email support</li>
                  <li className="text-emerald-300">✓ 30-day money-back guarantee</li>
                </ul>
                <ConciergeAddonCheckbox
                  tier="starter_pro"
                  price={CONCIERGE_ADDONS.starter_pro}
                  checked={!!conciergeChecked.starter_pro}
                  onChange={(v) => setConciergeChecked(c => ({ ...c, starter_pro: v }))}
                />
                <button
                  onClick={() => requestCheckout('starter_pro')}
                  disabled={checkingOut}
                  className="w-full bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-60 text-black py-2 text-xs font-black rounded transition-colors"
                  data-testid="mc-tier-starter-pro-cta"
                >
                  {checkingOut ? 'Opening Stripe…' : 'Start migration →'}
                </button>
                <p className="text-[10px] text-white/40 mt-2 text-center">Import2 charges up to $1,499 for the same size</p>
              </div>

              {/* Growth AI $1,799 — mid-market */}
              <div className="bg-white/5 border border-white/20 hover:border-white/40 p-5 rounded flex flex-col transition-colors" data-testid="mc-tier-growth-ai">
                <div className="text-xs text-white/60 mb-1 uppercase tracking-wider">Growth AI</div>
                <div className="text-3xl font-black mb-1">$1,799</div>
                <div className="text-xs text-white/40 mb-4">12,501 – 100,000 contacts</div>
                <ul className="text-xs text-white/70 space-y-1.5 mb-5 flex-1">
                  <li>✓ Everything in Starter Pro</li>
                  <li>✓ Priority email support</li>
                  <li>✓ Custom field mapping</li>
                  <li>✓ 60-day license</li>
                </ul>
                <ConciergeAddonCheckbox
                  tier="growth_ai"
                  price={CONCIERGE_ADDONS.growth_ai}
                  checked={!!conciergeChecked.growth_ai}
                  onChange={(v) => setConciergeChecked(c => ({ ...c, growth_ai: v }))}
                />
                <button
                  onClick={() => requestCheckout('growth_ai')}
                  disabled={checkingOut}
                  className="w-full bg-white/10 hover:bg-white/20 disabled:opacity-60 py-2 text-xs font-bold rounded transition-colors"
                  data-testid="mc-tier-growth-ai-cta"
                >
                  {checkingOut ? 'Opening Stripe…' : 'Choose Growth →'}
                </button>
                <p className="text-[10px] text-white/40 mt-2 text-center">Cheaper than competitor base $999 + their $875 note-cleanup add-on</p>
              </div>

              {/* Enterprise AI $4,499 */}
              <div className="bg-gradient-to-b from-purple-900/15 to-purple-950/25 border border-purple-400/30 hover:border-purple-300/60 p-5 rounded flex flex-col transition-colors" data-testid="mc-tier-enterprise-ai">
                <div className="text-xs text-purple-300 mb-1 uppercase tracking-wider">Enterprise AI</div>
                <div className="text-3xl font-black mb-1">$4,499</div>
                <div className="text-xs text-white/40 mb-4">100,001 – 500,000 contacts</div>
                <ul className="text-xs text-white/70 space-y-1.5 mb-5 flex-1">
                  <li>✓ Everything in Growth AI</li>
                  <li>✓ Custom destination included</li>
                  <li>✓ Dedicated Slack channel</li>
                  <li>✓ AI Migration Audit PDF</li>
                  <li>✓ 90-day license</li>
                </ul>
                <ConciergeAddonCheckbox
                  tier="enterprise_ai"
                  price={CONCIERGE_ADDONS.enterprise_ai}
                  checked={!!conciergeChecked.enterprise_ai}
                  onChange={(v) => setConciergeChecked(c => ({ ...c, enterprise_ai: v }))}
                />
                <button
                  onClick={() => requestCheckout('enterprise_ai')}
                  disabled={checkingOut}
                  className="w-full bg-purple-500/30 hover:bg-purple-500/50 disabled:opacity-60 text-purple-100 py-2 text-xs font-bold rounded transition-colors"
                  data-testid="mc-tier-enterprise-ai-cta"
                >
                  {checkingOut ? 'Opening Stripe…' : 'Choose Enterprise →'}
                </button>
                <p className="text-[10px] text-white/40 mt-2 text-center">Replaces competitor's $2,199+ "Custom Service" — but instant</p>
              </div>

              {/* Unlimited AI $7,999 */}
              <div className="bg-gradient-to-b from-emerald-900/15 to-emerald-950/25 border border-emerald-400/30 hover:border-emerald-300/60 p-5 rounded flex flex-col transition-colors" data-testid="mc-tier-unlimited-ai">
                <div className="text-xs text-emerald-300 mb-1 uppercase tracking-wider">Unlimited AI</div>
                <div className="text-3xl font-black mb-1">$7,999</div>
                <div className="text-xs text-white/40 mb-4">500,000+ contacts</div>
                <ul className="text-xs text-white/70 space-y-1.5 mb-5 flex-1">
                  <li>✓ Everything in Enterprise AI</li>
                  <li>✓ <strong>White-glove fulfillment</strong></li>
                  <li>✓ Dedicated migration engineer</li>
                  <li>✓ 180-day license</li>
                  <li className="text-emerald-300">✓ Prevents $20k+ manual repair</li>
                </ul>
                <p className="text-[11px] leading-snug text-emerald-300 mb-3" data-testid="mc-addon-unlimited-included">
                  ✓ Concierge included — we run it for you, no add-on needed.{' '}
                  <a href="/migrate/concierge" target="_blank" rel="noreferrer" className="underline text-white/50 hover:text-amber-300">How it works</a>
                </p>
                <button
                  onClick={() => requestCheckout('unlimited_ai')}
                  disabled={checkingOut}
                  className="w-full bg-emerald-500/30 hover:bg-emerald-500/50 disabled:opacity-60 text-emerald-100 py-2 text-xs font-bold rounded transition-colors"
                  data-testid="mc-tier-unlimited-ai-cta"
                >
                  {checkingOut ? 'Opening Stripe…' : 'Choose Unlimited →'}
                </button>
                <p className="text-[10px] text-white/40 mt-2 text-center">Flat fee prevents manual-cleanup costs at scale</p>
              </div>
            </div>

            {/* Concierge — done-for-you ADD-ON explainer under the DIY grid */}
            <div className="mt-6 bg-gradient-to-r from-[#F39C12]/15 to-transparent border border-[#F39C12]/50 rounded p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5" data-testid="mc-tier-concierge">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-[#F39C12] uppercase tracking-wider font-black">Concierge Migration Add-On</span>
                  <span className="bg-[#F39C12] text-black text-[10px] font-black px-2 py-0.5 rounded">WE DO IT FOR YOU</span>
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  Don&apos;t want to touch it at all? Check the <strong className="text-[#F39C12]">&ldquo;Add Concierge&rdquo;</strong> box
                  on any tier above and a migration specialist runs the whole thing with you on a scheduled
                  call — export, mapping, preflight, spot-check, signed audit certificate. Your old CRM is
                  never touched; we only read exports.
                </p>
                <p className="text-[11px] text-white/45 mt-1.5" data-testid="mc-concierge-pricing-note">
                  One flat price on top of your tier — no per-record charges: +$500 on Contacts Only &amp; Starter
                  Pro · +$750 on Growth AI · +$1,000 on Enterprise AI · included free with Unlimited AI.
                </p>
              </div>
              <div className="md:text-right shrink-0">
                <div className="text-3xl font-black mb-2">from +$500</div>
                <Link
                  to="/migrate/concierge"
                  className="inline-block w-full md:w-auto bg-[#F39C12] hover:bg-[#E08C00] text-black px-6 py-2.5 text-xs font-black rounded transition-colors text-center"
                  data-testid="mc-tier-concierge-cta"
                >
                  See exactly how it works →
                </Link>
                <p className="text-[10px] text-white/40 mt-2">Phone call scheduled at checkout</p>
              </div>
            </div>

            <p className="text-center text-xs text-white/50 mt-10">
              Have a license key? <button onClick={() => {
                const k = prompt('Enter your license key:');
                if (k) { setLicenseKey(k); checkLicense(k); }
              }} className="underline hover:text-white" data-testid="mc-enter-license">Enter it here</button>
              {licenseInfo?.valid && <span className="ml-2 text-green-400" data-testid="mc-license-active">✓ License active ({licenseInfo.tier})</span>}
            </p>
            <p className="text-center text-[11px] text-white/30 mt-3">
              Secure checkout via Stripe · Receipt emailed instantly · License keys delivered immediately
            </p>

            {/* SISTER-PRODUCT confident-subtle callout — converts price-sensitive
                visitors into SalesCrunchCRM subscribers ($30/user/mo recurring beats
                a one-time $599+ migration over 24 months). Transparent about
                the economics so it builds trust. */}
            <div
              className="mt-10 max-w-3xl mx-auto bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent border-l-2 border-emerald-400 px-5 py-4 rounded-r"
              data-testid="mc-salescrunch-callout"
            >
              <div className="flex items-start gap-3">
                <Sparkle size={18} weight="fill" className="text-emerald-300 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-white font-bold mb-1">Switching to SalesCrunchCRM? Migration is on us.</p>
                  <p className="text-xs text-white/70 leading-relaxed">
                    Any contact count. Any notes file. The full patent-pending AI Note Re-Association — free.
                    We make it back on the $30/user/mo subscription. SalesCrunchCRM is the ACT! you remember,
                    modernized — built for refugees who don't want to relearn a CRM.{' '}
                    <a href="https://act-reimagined.preview.emergentagent.com/pricing" target="_blank" rel="noreferrer"
                       className="text-emerald-300 hover:text-emerald-200 underline font-bold whitespace-nowrap"
                       data-testid="mc-salescrunch-callout-link">
                      See SalesCrunchCRM →
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pair-specific SEO link grid (only on the generic landing page).
            Gives Google in-app crawl paths to every /migrate/*-to-* URL
            and gives visitors a one-click jump to the page tailored to
            their exact migration. Filters by the visitor's selected
            source CRM — defaulting to "all sources" when set to generic. */}
        {!pairConfig && (() => {
          const allPairs = Object.values(MIGRATION_PAIRS);
          // When a real source is picked, narrow to that source's destinations.
          // Otherwise show every pair so Google still finds the full link tree.
          const filtered = (sourceCrm && sourceCrm !== 'generic')
            ? allPairs.filter(p => p.sourceId === sourceCrm)
            : allPairs;
          // Heading reflects the visitor's actual source: "Coming from Salesforce?"
          const sourceLabel = (sourceCrm && sourceCrm !== 'generic')
            ? (filtered[0]?.sourceName || sourceCrm)
            : 'any CRM';
          return (
          <section className="border-t border-white/10 px-6 py-12 bg-white/[0.02]" data-testid="mc-pair-link-grid">
            <div className="max-w-6xl mx-auto">
              <h3 className="text-lg font-black text-center mb-1">Coming from {sourceLabel}?</h3>
              <p className="text-center text-xs text-white/50 mb-6">Jump to the page built for your destination — pre-flight + AI note recovery already configured.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {filtered.map((p) => (
                  <a
                    key={p.slug}
                    href={`/migrate/${p.slug}`}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F39C12]/40 rounded px-3 py-2 text-xs text-white/70 hover:text-white transition-colors flex items-center justify-between"
                    data-testid={`mc-pair-link-${p.slug}`}
                  >
                    <span>{p.sourceName} → <strong className="text-white">{p.destName}</strong></span>
                    <ArrowRight size={12} weight="bold" className="text-[#F39C12]/60" />
                  </a>
                ))}
              </div>
            </div>
          </section>
          );
        })()}

        {/* Related migrations cross-link cluster — only on pair pages.
            For SEO: gives every /migrate/{pair} page 6 outbound internal
            links to topically-clustered sibling pairs (3 same-source +
            3 same-destination). Creates the link mesh Google rewards over
            orphan leaf pages. See marketing/cross-linking-implementation-lindy.md
            for the rationale we explained externally. */}
        {pairConfig && (() => {
          const allPairs = Object.values(MIGRATION_PAIRS);
          const sameSource = allPairs
            .filter(p => p.sourceId === pairConfig.sourceId && p.destId !== pairConfig.destId && p.destId !== 'generic')
            .slice(0, 3);
          const sameDest = allPairs
            .filter(p => p.destId === pairConfig.destId && p.sourceId !== pairConfig.sourceId)
            .slice(0, 3);
          if (sameSource.length === 0 && sameDest.length === 0) return null;
          return (
            <section
              className="border-t border-white/10 px-6 py-14 bg-white/[0.02]"
              data-testid="mc-pair-related"
              aria-labelledby="related-migrations-heading"
            >
              <div className="max-w-6xl mx-auto">
                <h3
                  id="related-migrations-heading"
                  className="text-lg font-black text-center mb-1"
                >
                  Related migrations
                </h3>
                <p className="text-center text-xs text-white/50 mb-8">
                  Same patent-pending AI note recovery — pre-configured for these other combos.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {sameSource.length > 0 && (
                    <div data-testid="mc-related-same-source">
                      <p className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-3">
                        Other migrations from {pairConfig.sourceName}
                      </p>
                      <div className="space-y-2">
                        {sameSource.map((p) => (
                          <a
                            key={p.slug}
                            href={`/migrate/${p.slug}`}
                            className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F39C12]/40 rounded px-4 py-2.5 text-sm text-white/75 hover:text-white transition-colors flex items-center justify-between"
                            data-testid={`mc-related-src-${p.slug}`}
                          >
                            <span>{p.sourceName} → <strong className="text-white">{p.destName}</strong></span>
                            <ArrowRight size={14} weight="bold" className="text-[#F39C12]/60" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {sameDest.length > 0 && (
                    <div data-testid="mc-related-same-dest">
                      <p className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-3">
                        Other migrations to {pairConfig.destName}
                      </p>
                      <div className="space-y-2">
                        {sameDest.map((p) => (
                          <a
                            key={p.slug}
                            href={`/migrate/${p.slug}`}
                            className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F39C12]/40 rounded px-4 py-2.5 text-sm text-white/75 hover:text-white transition-colors flex items-center justify-between"
                            data-testid={`mc-related-dest-${p.slug}`}
                          >
                            <span><strong className="text-white">{p.sourceName}</strong> → {p.destName}</span>
                            <ArrowRight size={14} weight="bold" className="text-[#F39C12]/60" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-center mt-8">
                  <a
                    href="/migrate"
                    className="text-xs text-white/50 hover:text-white/80 underline-offset-2 hover:underline transition-colors"
                    data-testid="mc-related-see-all"
                  >
                    See all 56 migration combinations →
                  </a>
                </div>
              </div>
            </section>
          );
        })()}

        {/* Footer (shared with /migrate/compare and /migrate/:pair pages) */}
        <MigrationCrunchFooter />
        <MigrationConciergeFloat />

        {/* Email-gate: prevents typo'd checkout emails. Mounted at root so
            it overlays the entire landing surface including pricing tiers. */}
        <EmailGateModal
          open={!!pendingTier}
          tierLabel={pendingTier ? TIER_LABELS[pendingTier] : ''}
          onCancel={handleEmailGateCancel}
          onConfirm={handleEmailGateConfirm}
        />

        {/* Concierge intake — phone + call window collected BEFORE Stripe.
            Opens when a tier CTA is clicked with "Add Concierge" checked. */}
        <ConciergeIntakeModal
          open={!!conciergeTier}
          busy={checkingOut}
          tierName={conciergeTier ? TIER_PRICING[conciergeTier].name : ''}
          tierAmount={conciergeTier ? TIER_PRICING[conciergeTier].amount : 0}
          addonAmount={conciergeTier ? CONCIERGE_ADDONS[conciergeTier] : 0}
          onCancel={() => setConciergeTier(null)}
          onConfirm={startConciergeCheckout}
        />

        {/* Stripe checkout success modal — appears on return from Stripe with paid session */}
        {checkoutSuccess && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            data-testid="mc-checkout-success"
            onClick={() => setCheckoutSuccess(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0a0e27] border-2 border-[#F39C12] rounded-lg max-w-md w-full p-7 relative"
            >
              <button
                onClick={() => setCheckoutSuccess(null)}
                className="absolute top-3 right-3 text-white/40 hover:text-white"
                data-testid="mc-checkout-success-close"
              >
                <X size={20} weight="bold" />
              </button>
              <div className="text-center mb-5">
                <CheckCircle size={48} weight="fill" className="text-emerald-400 mx-auto mb-3" />
                <h3 className="text-2xl font-black mb-1">{(checkoutSuccess.concierge || checkoutSuccess.tier === 'concierge') ? "You're booked 🎉" : 'Payment received 🎉'}</h3>
                <p className="text-sm text-white/70">
                  {(checkoutSuccess.concierge || checkoutSuccess.tier === 'concierge') ? (
                    <>Your <strong className="text-[#F39C12]">Concierge Migration</strong> is confirmed and paid.</>
                  ) : (
                    <>Your <strong className="text-[#F39C12]">{
                      {
                        contacts_only: 'Contacts Only',
                        starter_pro: 'Starter Pro',
                        growth_ai: 'Growth AI',
                        enterprise_ai: 'Enterprise AI',
                        unlimited_ai: 'Unlimited AI',
                      }[checkoutSuccess.tier] || 'MigrationCrunch'
                    }</strong> migration license is ready.</>
                  )}
                </p>
              </div>
              <div className="bg-white/5 border border-white/15 rounded p-3 mb-4">
                <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">License Key</div>
                <div className="flex items-center gap-2">
                  <code className="text-base font-mono font-bold text-emerald-300 flex-1" data-testid="mc-license-key-display">
                    {checkoutSuccess.license_key}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(checkoutSuccess.license_key);
                    }}
                    className="text-white/60 hover:text-white p-1.5 hover:bg-white/10 rounded"
                    title="Copy"
                    data-testid="mc-copy-license"
                  >
                    <Copy size={14} weight="bold" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-white/60 mb-5">
                Save this key — it's also stored on this browser. A receipt was emailed to{' '}
                <strong className="text-white">{checkoutSuccess.email || 'your inbox'}</strong>.
                {checkoutSuccess.tier === 'unlimited_ai' && (
                  <span className="block mt-2 text-emerald-300">
                    📞 We'll email you within 1 business day to scope your dedicated engineer.
                  </span>
                )}
                {(checkoutSuccess.concierge || checkoutSuccess.tier === 'concierge') && (
                  <span className="block mt-2 text-emerald-300" data-testid="mc-concierge-success-note">
                    📞 Nothing else to do — we'll confirm your call time by email within a few business
                    hours and run the whole migration with you on that call. One flat price — no
                    per-record charges, no follow-up invoices.
                  </span>
                )}
              </p>
              <button
                onClick={() => { setCheckoutSuccess(null); fileInput.current?.click(); }}
                className="w-full bg-[#F39C12] hover:bg-[#E08C00] text-black py-2.5 text-sm font-black rounded transition-colors"
                data-testid="mc-checkout-success-start"
              >
                Upload my file → Start migrating
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── WIZARD (steps 1-3) — same styling as in-app ────────────
  return (
    <div className="min-h-screen bg-[#0a0e27] text-white">
      <header className="border-b border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database size={20} weight="fill" className="text-[#F39C12]" />
          <span className="font-black">Migration<span className="text-[#F39C12]">Crunch</span></span>
        </div>
        <button onClick={reset} className="text-sm text-white/60 hover:text-white flex items-center gap-1">
          <X size={14} weight="bold" />Start over
        </button>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 mb-4 text-sm" data-testid="mc-error">
            <WarningCircle size={14} weight="fill" className="inline mr-1" />{error}
          </div>
        )}

        {/* STEP 1: Mappings */}
        {step === 1 && session && (
          <div data-testid="mc-step-mappings">
            <h2 className="text-2xl font-black mb-2">AI mapped your fields</h2>
            <p className="text-white/60 text-sm mb-1">
              <strong className="text-white">{session.row_count.toLocaleString()}</strong> rows from <strong className="text-white">{session.filename}</strong>
              {session.needs_license && <span className="ml-2 text-amber-400">⚠ Free tier exports first {session.free_tier_limit} only</span>}
              {session.tier_limit_exceeded && session.recommended_tier && (
                <span className="ml-2 text-amber-400" data-testid="mc-tier-warn-inline">
                  ⚠ Your {session.license?.tier} license covers {Number(session.tier_contact_limit).toLocaleString()} — upgrade below
                </span>
              )}
            </p>
            <p className="text-white/60 text-sm mb-5">Adjust anything that looks wrong, then continue.</p>

            {(session.ai_warnings || []).length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 px-3 py-2 mb-3 text-xs">
                <strong>AI noted:</strong>
                <ul className="list-disc pl-5 mt-1">
                  {session.ai_warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className="bg-white/5 border border-white/10 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr className="text-left text-xs uppercase tracking-wide text-white/60">
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Sample</th>
                    <th className="px-3 py-2">Maps to</th>
                    <th className="px-3 py-2">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {session.columns.map(col => {
                    const sample = (session.sample_rows || []).slice(0, 3).map(r => r[col]).filter(Boolean).join(' · ').slice(0, 50);
                    const ai = session.ai_mappings?.[col];
                    const conf = ai?.confidence || 0;
                    const expanded = expandedSamples[col];
                    return (
                      <tr key={col} className="border-t border-white/10" data-testid={`mc-map-${col}`}>
                        <td className="px-3 py-2 font-medium">{col}</td>
                        <td className="px-3 py-2 text-white/50 max-w-[180px] align-top">
                          <div className="truncate">{sample || '—'}</div>
                          {conf < 0.85 && (
                            expanded === undefined ? (
                              <button
                                onClick={() => loadColumnSamples(col)}
                                className="text-[10px] text-amber-300 hover:text-amber-200 underline underline-offset-2 mt-0.5"
                                data-testid={`mc-more-samples-${col}`}
                              >
                                See more values ↓
                              </button>
                            ) : expanded === 'loading' ? (
                              <span className="text-[10px] text-white/40">Loading…</span>
                            ) : (
                              <div className="mt-1.5 bg-black/40 border border-amber-400/25 rounded p-2 max-h-40 overflow-y-auto" data-testid={`mc-samples-panel-${col}`}>
                                <div className="text-[10px] text-white/40 mb-1">
                                  {expanded.non_empty_count.toLocaleString()} of {expanded.total_rows.toLocaleString()} rows have a value · first {expanded.samples.length} distinct:
                                </div>
                                <ul className="space-y-0.5">
                                  {expanded.samples.map((s, i) => (
                                    <li key={i} className="text-[11px] text-white/70 break-all leading-snug">{s}</li>
                                  ))}
                                </ul>
                                <button
                                  onClick={() => setExpandedSamples(es => { const n = { ...es }; delete n[col]; return n; })}
                                  className="text-[10px] text-white/40 hover:text-white/70 underline mt-1"
                                  data-testid={`mc-collapse-samples-${col}`}
                                >
                                  collapse ↑
                                </button>
                              </div>
                            )
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={mappings[col] || 'skip'}
                            onChange={(e) => setMappings(m => ({ ...m, [col]: e.target.value }))}
                            className="bg-[#0a0e27] border border-white/20 px-2 py-1 text-xs"
                            data-testid={`mc-select-${col}`}
                          >
                            <option value="skip">— Skip —</option>
                            {(session.canonical_fields || []).map(f => <option key={f.id} value={f.id}>{f.id}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
                              <div className={`h-full ${conf >= 0.85 ? 'bg-green-500' : conf >= 0.5 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.round(conf * 100)}%` }} />
                            </div>
                            <span className="text-xs text-white/60">{Math.round(conf * 100)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={reset} className="px-4 py-2 text-sm border border-white/20 hover:bg-white/5">Cancel</button>
              <button onClick={runPreflight} disabled={busy} className="px-5 py-2 text-sm font-bold bg-[#F39C12] hover:bg-[#E08C00] text-black disabled:opacity-50 flex items-center gap-1" data-testid="mc-next-btn">
                {busy ? 'Checking...' : <>Continue<ArrowRight size={14} weight="bold" /></>}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Pre-flight + destination */}
        {step === 2 && preflight && (
          <div data-testid="mc-step-preflight">
            <h2 className="text-2xl font-black mb-4">Pre-flight check</h2>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <Stat label="Total rows" value={preflight.total_rows} />
              <Stat label="Duplicates" value={preflight.duplicate_count || 0} warn={preflight.duplicate_count > 0} />
              <Stat label="Missing email" value={preflight.missing_email_count || 0} />
              <Stat label="Bad email" value={preflight.malformed_email_count || 0} warn={preflight.malformed_email_count > 0} />
              <Stat label="Missing phone" value={preflight.missing_phone_count || 0} />
              <Stat label="Missing name" value={preflight.rows_missing_name_count || 0} warn={preflight.rows_missing_name_count > 0} />
            </div>

            {preflight.duplicate_count > 0 && !dupResolution && (
              <div className="bg-amber-500/10 border border-amber-500/40 px-4 py-3 mb-6 rounded" data-testid="mc-dup-banner">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-bold text-amber-300 text-sm">
                      ⚠ {preflight.duplicate_count} duplicate {preflight.duplicate_count === 1 ? 'row' : 'rows'} detected
                    </div>
                    <div className="text-xs text-white/70 mt-0.5">
                      Pick a master record for each group — notes from the duplicates get merged onto it, so nothing is lost.
                    </div>
                  </div>
                  <button
                    onClick={loadDuplicates}
                    disabled={dupBusy}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black text-sm font-black rounded disabled:opacity-50"
                    data-testid="mc-resolve-dupes-btn"
                  >
                    {dupBusy ? 'Loading…' : 'Resolve duplicates →'}
                  </button>
                </div>
              </div>
            )}

            {dupResolution && (
              <div className="bg-emerald-500/10 border border-emerald-400/40 px-4 py-3 mb-6 rounded text-sm" data-testid="mc-dup-resolved-banner">
                <span className="text-emerald-300 font-bold">✓ Duplicates resolved:</span>{' '}
                <span className="text-white/80">
                  {dupResolution.merged_groups} group{dupResolution.merged_groups === 1 ? '' : 's'} merged
                  {' · '}{dupResolution.rows_removed} duplicate row{dupResolution.rows_removed === 1 ? '' : 's'} removed
                  {' · '}{dupResolution.notes_merged} note{dupResolution.notes_merged === 1 ? '' : 's'} consolidated
                  {dupResolution.fields_filled > 0 && <> · {dupResolution.fields_filled} blank field{dupResolution.fields_filled === 1 ? '' : 's'} filled</>}
                  {' · '}{dupResolution.remaining_rows.toLocaleString()} rows remain
                </span>
                <button onClick={loadDuplicates} disabled={dupBusy} className="ml-2 underline text-white/60 hover:text-white text-xs" data-testid="mc-redo-dupes-btn">
                  Redo
                </button>
              </div>
            )}

            <h3 className="text-lg font-bold mb-2">Choose your destination CRM</h3>
            <p className="text-white/60 text-sm mb-3">
              We'll format the export with column headers that destination CRM expects.{' '}
              <span className="text-white/40">Don't see yours? Pick "Generic CSV" — it works with every CRM that imports CSV.</span>
            </p>
            <div className="grid sm:grid-cols-2 gap-2 mb-6">
              {presets.map(p => {
                const isFreeDest = p.id === 'salescrunch';
                const selected = destination === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setDestination(p.id)}
                    className={`text-left px-4 py-3 border rounded relative ${
                      selected
                        ? (isFreeDest ? 'bg-emerald-500/20 border-emerald-400' : 'bg-[#F39C12]/20 border-[#F39C12]')
                        : (isFreeDest ? 'bg-emerald-500/5 border-emerald-500/40 hover:border-emerald-400' : 'bg-white/5 border-white/10 hover:border-white/30')
                    }`}
                    data-testid={`mc-preset-${p.id}`}
                  >
                    {isFreeDest && (
                      <span className="absolute top-1.5 right-2 bg-emerald-400 text-black text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider">
                        Free
                      </span>
                    )}
                    <div className="font-semibold text-sm">{p.name.replace(' · FREE', '')}</div>
                    {isFreeDest && (
                      <div className="text-[11px] text-emerald-300/80 mt-0.5">Unlimited contacts · No license required</div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* SISTER-PRODUCT free callout — appears when SalesCrunchCRM is the
                selected destination. Overrides the paid upgrade prompts since
                this migration is free regardless of contact count. */}
            {destination === 'salescrunch' && (
              <div
                className="bg-gradient-to-r from-emerald-500/15 to-emerald-600/10 border border-emerald-400/50 text-emerald-100 px-4 py-3 mb-4 rounded"
                data-testid="mc-salescrunch-free-banner"
              >
                <div className="font-black text-white mb-1 text-sm flex items-center gap-2">
                  <Sparkle size={14} weight="fill" className="text-emerald-300" />
                  Migration to SalesCrunchCRM is on us.
                </div>
                <div className="text-xs text-white/85 leading-relaxed">
                  <strong>{session?.row_count?.toLocaleString() || 'All'} contacts</strong>, all your notes, the full AI Note Re-Association — free.
                  We make it back on the $30/user/mo subscription. Fair trade.
                </div>
                <a
                  href="https://act-reimagined.preview.emergentagent.com/pricing"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-emerald-300 hover:text-emerald-200 underline mt-1.5 inline-block"
                  data-testid="mc-salescrunch-learn-more"
                >
                  Why SalesCrunchCRM? (The ACT! you remember, modernized) →
                </a>
              </div>
            )}

            {session?.needs_license && destination !== 'salescrunch' && (
              <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 px-3 py-2 mb-4 text-sm" data-testid="mc-needs-license-banner">
                ⚠ Free tier — only the first <strong>{session.free_tier_limit}</strong> contacts will be in the download.
                You have <strong>{session.row_count.toLocaleString()}</strong> total.{' '}
                {session.recommended_tier ? (
                  <button
                    onClick={() => requestCheckout(session.recommended_tier.tier)}
                    disabled={checkingOut}
                    className="underline font-bold hover:text-white disabled:opacity-50"
                    data-testid="mc-upgrade-cta-free"
                  >
                    Unlock all {session.row_count.toLocaleString()} with {session.recommended_tier.label} (${Number(session.recommended_tier.amount).toLocaleString()}) →
                  </button>
                ) : (
                  <button className="underline" onClick={() => {
                    const k = prompt('Enter license key:');
                    if (k) { setLicenseKey(k); checkLicense(k); }
                  }}>Enter license key</button>
                )}
              </div>
            )}

            {session?.tier_limit_exceeded && session?.recommended_tier && destination !== 'salescrunch' && (
              <div
                className="bg-gradient-to-r from-amber-500/15 to-emerald-500/15 border border-emerald-400/40 text-emerald-100 px-4 py-3 mb-4 text-sm rounded"
                data-testid="mc-tier-upgrade-banner"
              >
                <div className="font-bold text-white mb-1">
                  ⚡ You'll leave {(session.row_count - session.tier_contact_limit).toLocaleString()} contacts behind
                </div>
                <div className="text-xs text-white/80 mb-3">
                  Your <strong>{session.license?.tier}</strong> license covers <strong>{Number(session.tier_contact_limit).toLocaleString()}</strong> contacts, but your file has <strong>{session.row_count.toLocaleString()}</strong>.
                  Without upgrading, your download will be truncated to {Number(session.tier_contact_limit).toLocaleString()} rows.
                </div>
                <button
                  onClick={() => requestCheckout(session.recommended_tier.tier)}
                  disabled={checkingOut}
                  className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black px-4 py-2 text-xs font-black rounded transition-colors"
                  data-testid="mc-upgrade-cta-tier"
                >
                  {checkingOut ? 'Opening Stripe…' : `Upgrade to ${session.recommended_tier.label} ($${Number(session.recommended_tier.amount).toLocaleString()}) — covers ${Number(session.recommended_tier.contact_limit).toLocaleString()} →`}
                </button>
              </div>
            )}

            <div className="flex justify-between gap-2">
              <button onClick={() => setStep(1)} className="px-4 py-2 text-sm border border-white/20 hover:bg-white/5">← Back</button>
              <button onClick={downloadCsv} disabled={busy} className="px-6 py-2 text-sm font-bold bg-green-500 hover:bg-green-600 text-black disabled:opacity-50 flex items-center gap-2" data-testid="mc-download-btn">
                <FileArrowDown size={16} weight="bold" />
                {busy ? 'Generating...' : 'Download cleaned CSV'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2.5: Resolve duplicates */}
        {step === 2.5 && dupGroups && (
          <div data-testid="mc-step-duplicates">
            <h2 className="text-2xl font-black mb-1">Resolve duplicates</h2>
            <p className="text-white/60 text-sm mb-4">
              <strong className="text-white">{dupGroups.total_groups}</strong> duplicate group{dupGroups.total_groups === 1 ? '' : 's'} found.
              Click the row you want to keep as the <strong className="text-white">master</strong> — notes from the other rows are appended
              onto it and blank fields get filled in. Nothing is lost.
            </p>
            {dupGroups.total_groups > dupGroups.shown_groups && (
              <div className="text-xs text-amber-300 mb-3" data-testid="mc-dup-truncated-note">
                Showing the first {dupGroups.shown_groups} groups — "Merge all automatically" handles every group.
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
              <button
                onClick={autoMergeAll}
                disabled={dupBusy}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-black rounded disabled:opacity-50"
                data-testid="mc-merge-all-btn"
              >
                {dupBusy ? 'Merging…' : `⚡ Merge all automatically (${dupGroups.total_groups} group${dupGroups.total_groups === 1 ? '' : 's'})`}
              </button>
              <div className="text-[11px] text-white/50">Auto-merge keeps the most complete row in each group as the master.</div>
            </div>

            <div className="space-y-4 mb-6">
              {dupGroups.groups.map((g, gi) => {
                const choice = dupChoices[g.key] || { master_index: g.suggested_master_index, action: 'merge' };
                const keepBoth = choice.action === 'keep_both';
                return (
                  <div
                    key={g.key}
                    className={`border rounded p-4 ${keepBoth ? 'border-white/10 bg-white/[0.02] opacity-70' : 'border-white/15 bg-white/5'}`}
                    data-testid={`mc-dup-group-${gi}`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                      <div className="text-xs font-bold uppercase tracking-widest text-white/50">
                        Group {gi + 1} · {g.rows.length} matching rows
                      </div>
                      <label className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer hover:text-white">
                        <input
                          type="checkbox"
                          checked={keepBoth}
                          onChange={() => setDupChoices(c => ({ ...c, [g.key]: { ...choice, action: keepBoth ? 'merge' : 'keep_both' } }))}
                          data-testid={`mc-keep-both-${gi}`}
                        />
                        Keep all — these are different people
                      </label>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {g.rows.map(r => (
                        <DupRowCard
                          key={r.index}
                          row={r}
                          isMaster={!keepBoth && choice.master_index === r.index}
                          suggested={g.suggested_master_index === r.index}
                          disabled={keepBoth}
                          onSelect={() => setDupChoices(c => ({ ...c, [g.key]: { master_index: r.index, action: 'merge' } }))}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between gap-2">
              <button onClick={() => setStep(2)} className="px-4 py-2 text-sm border border-white/20 hover:bg-white/5" data-testid="mc-dup-back-btn">
                ← Back to pre-flight
              </button>
              <button
                onClick={applyResolutions}
                disabled={dupBusy}
                className="px-6 py-2 text-sm font-bold bg-green-500 hover:bg-green-600 text-black disabled:opacity-50"
                data-testid="mc-apply-merges-btn"
              >
                {dupBusy ? 'Merging…' : 'Apply merges & continue →'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Done */}
        {step === 3 && (
          <div className="py-12" data-testid="mc-step-done">
            <div className="text-center">
              <CheckCircle size={64} weight="fill" className="mx-auto text-green-400 mb-4" />
              <h2 className="text-3xl font-black mb-3">Your file is ready 🎉</h2>
              <p className="text-white/70 mb-6 max-w-md mx-auto">
                Check your downloads. Import it into <strong className="text-white">{presets.find(p => p.id === destination)?.name || destination}</strong> using their normal CSV import.
              </p>
            </div>

            {/* ── Crunchy Audit — free Data Integrity Report ── */}
            <div className="max-w-2xl mx-auto mb-8 bg-white/5 border border-white/10 p-5 text-left" data-testid="mc-audit-panel">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <h3 className="text-lg font-black">🔍 Crunchy Audit — Data Integrity Report</h3>
                <span className="text-[10px] font-bold bg-green-500/20 text-green-400 px-2 py-0.5 tracking-widest">FREE</span>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Don&apos;t spend weeks in Excel proving your data moved correctly. Crunchy re-derives every
                exported record from your source file and cross-verifies it — including cryptographic
                round-trip validation of the AI notes payload.
              </p>
              {!audit ? (
                <button onClick={runAudit} disabled={auditBusy}
                  className="px-5 py-2.5 bg-[#F39C12] hover:bg-[#E08C00] text-black text-sm font-bold disabled:opacity-50"
                  data-testid="mc-run-audit-btn">
                  {auditBusy ? 'Auditing every record…' : 'Run the free audit'}
                </button>
              ) : (
                <div data-testid="mc-audit-result">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <Stat label="Records checked" value={audit.exported_rows.toLocaleString()} />
                    <Stat label="Verified clean" value={audit.verified_rows.toLocaleString()} />
                    <Stat label="Flagged" value={audit.flagged_row_count.toLocaleString()} warn={audit.flagged_row_count > 0} />
                    <Stat label="Integrity score" value={`${audit.integrity_score}%`} warn={audit.integrity_score < 100} />
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {audit.checks.map(c => (
                      <li key={c.id} className="text-xs flex gap-2" data-testid={`mc-audit-check-${c.id}`}>
                        <span className={
                          c.status === 'pass' ? 'text-green-400' :
                          c.status === 'warn' ? 'text-amber-400' :
                          c.status === 'fail' ? 'text-red-400' : 'text-white/40'
                        }>
                          {c.status === 'pass' ? '✓' : c.status === 'warn' ? '⚠' : c.status === 'fail' ? '✗' : '–'}
                        </span>
                        <span><b>{c.label}:</b> <span className="text-white/70">{c.summary}</span></span>
                      </li>
                    ))}
                  </ul>
                  {audit.flagged_rows.length > 0 && (
                    <div className="bg-amber-500/10 border border-amber-500/30 p-3 mb-4 max-h-40 overflow-y-auto">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-amber-400 mb-1.5">
                        Flagged rows (first {Math.min(audit.flagged_rows.length, 10)})
                      </div>
                      {audit.flagged_rows.slice(0, 10).map(f => (
                        <div key={f.row} className="text-xs text-white/70 mb-0.5">
                          <b className="text-white">Row {f.row}:</b> {f.issues.join('; ')}
                        </div>
                      ))}
                    </div>
                  )}
                  <a href={`${API_URL}/api/migrate-pro/${session.session_id}/integrity/${destination}/certificate`}
                    className="inline-block px-5 py-2 bg-white/10 hover:bg-white/20 text-sm font-bold"
                    data-testid="mc-audit-certificate-link">
                    ⬇ Download Integrity Certificate
                  </a>
                </div>
              )}
            </div>

            <div className="text-center">
              <div className="bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] px-4 py-3 inline-block text-sm mb-6 max-w-md">
                💡 With a license key, you also unlock unlimited contacts + separate notes-file matching (upload your CRM&apos;s HISTORY.dbf or similar). Right now your free preview already includes full note extraction on the first 50.
              </div>
              <div className="flex items-center justify-center gap-3">
                <button onClick={reset} className="px-5 py-2 bg-white/10 hover:bg-white/20 text-sm font-bold">
                  Migrate another file
                </button>
                <a href="https://salescrunchcrm.com" className="px-5 py-2 bg-[#F39C12] hover:bg-[#E08C00] text-black text-sm font-bold">
                  Try SalesCrunchCRM →
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
      <MigrationConciergeFloat />
    </div>
  );
}

function DupRowCard({ row, isMaster, suggested, disabled, onSelect }) {
  const d = row.data || {};
  const name = [d.first_name, d.last_name].filter(Boolean).join(' ') || d.full_name || '(no name)';
  const fields = [
    ['Company', d.company], ['Title', d.title], ['Email', d.email],
    ['Phone', d.phone], ['Mobile', d.mobile],
    ['Location', [d.address_city, d.address_state].filter(Boolean).join(', ')],
    ['Tags', d.tags],
  ].filter(([, v]) => v);
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={`text-left p-3 rounded border transition-colors ${
        isMaster ? 'border-emerald-400 bg-emerald-500/15' : 'border-white/10 bg-black/20 hover:border-white/30'
      } ${disabled ? 'cursor-default' : ''}`}
      data-testid={`mc-dup-row-${row.index}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="font-bold text-sm truncate">{name}</span>
        <span className="flex items-center gap-1 shrink-0">
          {isMaster && (
            <span data-testid="mc-dup-master-badge" className="text-[9px] font-black bg-emerald-400 text-black px-1.5 py-0.5 rounded uppercase tracking-wider">Master</span>
          )}
          {!isMaster && suggested && !disabled && (
            <span className="text-[9px] font-bold text-white/40 border border-white/20 px-1.5 py-0.5 rounded uppercase tracking-wider">Most complete</span>
          )}
        </span>
      </div>
      <div className="space-y-0.5">
        {fields.map(([k, v]) => (
          <div key={k} className="text-[11px] text-white/70 truncate"><span className="text-white/40">{k}:</span> {v}</div>
        ))}
      </div>
      {d.notes && (
        <div className="mt-1.5 text-[11px] text-white/50 line-clamp-2 border-t border-white/10 pt-1.5">📝 {d.notes}</div>
      )}
      <div className="mt-1.5 flex items-center justify-between text-[10px]">
        <span className="text-white/40">{row.filled_fields} fields filled · File row {row.index + 2}</span>
        {isMaster
          ? <span className="text-emerald-300/90 font-bold">Kept · notes merge in</span>
          : !disabled && <span className="text-amber-300/80">Notes merge into master · row removed</span>}
      </div>
    </button>
  );
}

function Stat({ label, value, warn }) {
  const color = warn ? 'text-amber-400' : 'text-[#F39C12]';
  return (
    <div className="bg-white/5 border border-white/10 p-3 rounded text-center">
      <div className={`text-2xl font-black ${color}`}>{value}</div>
      <div className="text-[10px] uppercase text-white/50 tracking-wide mt-0.5">{label}</div>
    </div>
  );
}
